/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Atlas API
 * REST contract for Atlas (docs/adr/2026-09-28-api-contract-openapi-orval.md). The web UI is a client of this contract with no private backchannel. Wire rules: snake_case fields, enum values verbatim, RFC 3339 UTC timestamps, one error envelope. 404 means "not found or not in your scope". State-changing requests need the X-CSRF-Token header (value of the atlas_csrf cookie) and a same-origin Origin header.
 *
 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { apiFetch } from './fetcher';
/**
 * Durations in seconds. availability = (up + degraded + critical) / (up + degraded + critical + down); within_thresholds = up / the same. Both are null when nothing was measured.
 */
export interface SlaFigures {
  up_seconds: number;
  degraded_seconds: number;
  critical_seconds: number;
  down_seconds: number;
  maintenance_seconds: number;
  unmeasured_seconds: number;
  outages: number;
  longest_outage_seconds: number;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  availability: number | null;
  /**
     * @minimum 0
     * @maximum 1
     * @nullable
     */
  within_thresholds: number | null;
}

export interface SlaLinkRow {
  id: string;
  name: string;
  /** Monitors in the report that measure this link */
  monitors: number;
  figures: SlaFigures;
}

export type CheckType = typeof CheckType[keyof typeof CheckType];


export const CheckType = {
  ICMP: 'ICMP',
  TCP: 'TCP',
  HTTP: 'HTTP',
  PATH: 'PATH',
} as const;

export interface SlaMonitorRow {
  id: string;
  name: string;
  check_type: CheckType;
  is_active: boolean;
  /**
     * Set only when the link is in the report too
     * @nullable
     */
  link_id: string | null;
  figures: SlaFigures;
}

export interface SlaReport {
  month: string;
  time_zone: string;
  from: string;
  /** End of the month, or now for the month in progress */
  to: string;
  partial: boolean;
  generated_at: string;
  /**
     * The customer the report is for; null for the report over everything
     * @nullable
     */
  tenant_id: string | null;
  /** @nullable */
  tenant_name: string | null;
  links: SlaLinkRow[];
  monitors: SlaMonitorRow[];
}

export type ErrorError = {
  code: string;
  message: string;
};

export interface Error {
  error: ErrorError;
}

export type Role = typeof Role[keyof typeof Role];


export const Role = {
  ADMIN: 'ADMIN',
  NOC: 'NOC',
  CUSTOMER: 'CUSTOMER',
} as const;

export interface LoginRequest {
  email: string;
  password: string;
}

export interface Me {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  /** @nullable */
  tenant_id?: string | null;
}

export interface Tenant {
  id: string;
  name: string;
  created_at: string;
}

export interface TenantInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
}

export type NodeType = typeof NodeType[keyof typeof NodeType];


export const NodeType = {
  REGION: 'REGION',
  CITY: 'CITY',
  POP: 'POP',
  DATACENTER: 'DATACENTER',
  HALL: 'HALL',
  MMR: 'MMR',
  RACK: 'RACK',
  DEVICE: 'DEVICE',
} as const;

/**
 * @nullable
 */
export type NodeLayout = { [key: string]: unknown } | null;

export interface Node {
  id: string;
  name: string;
  slug: string;
  type: NodeType;
  /** @nullable */
  parent_id: string | null;
  /** @nullable */
  latitude?: number | null;
  /** @nullable */
  longitude?: number | null;
  /** @nullable */
  layout?: NodeLayout;
  /** @nullable */
  external_ref?: string | null;
  /** @nullable */
  tenant_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * @nullable
 */
export type NodeInputLayout = { [key: string]: unknown } | null;

export interface NodeInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * URL segment, unique among siblings. Derived from name when omitted.
     * @pattern ^[a-z0-9][a-z0-9-]{0,62}$
     */
  slug?: string;
  type: NodeType;
  /** @nullable */
  parent_id?: string | null;
  /** @nullable */
  latitude?: number | null;
  /** @nullable */
  longitude?: number | null;
  /** @nullable */
  layout?: NodeInputLayout;
  /** @nullable */
  external_ref?: string | null;
  /** @nullable */
  tenant_id?: string | null;
}

export type LinkType = typeof LinkType[keyof typeof LinkType];


export const LinkType = {
  SUBSEA: 'SUBSEA',
  TERRESTRIAL: 'TERRESTRIAL',
  METRO_FIBER: 'METRO_FIBER',
  CROSS_CONNECT: 'CROSS_CONNECT',
  TRANSIT: 'TRANSIT',
  PEERING: 'PEERING',
} as const;

export interface Link {
  id: string;
  name: string;
  a_node_id: string;
  z_node_id: string;
  link_type: LinkType;
  /** @nullable */
  provider?: string | null;
  /** @nullable */
  circuit_id?: string | null;
  /** @nullable */
  capacity_mbps?: number | null;
  /** @nullable */
  expected_rtt_ms?: number | null;
  /** @nullable */
  depends_on_link_id?: string | null;
  /** @nullable */
  tenant_id?: string | null;
  /** @nullable */
  external_ref?: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  a_node_id: string;
  z_node_id: string;
  link_type: LinkType;
  /** @nullable */
  provider?: string | null;
  /** @nullable */
  circuit_id?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  capacity_mbps?: number | null;
  /** @nullable */
  expected_rtt_ms?: number | null;
  /** @nullable */
  depends_on_link_id?: string | null;
  /** @nullable */
  tenant_id?: string | null;
  /** @nullable */
  external_ref?: string | null;
}

/**
 * site: a node the caller may see by name; atlas: an anonymous stand-in for internal nodes
 */
export type NetworkPlaceKind = typeof NetworkPlaceKind[keyof typeof NetworkPlaceKind];


export const NetworkPlaceKind = {
  site: 'site',
  atlas: 'atlas',
} as const;

export interface NetworkPlace {
  /** A node id (kind site), or atlas:<city> for an anonymous place */
  id: string;
  name: string;
  /** site: a node the caller may see by name; atlas: an anonymous stand-in for internal nodes */
  kind: NetworkPlaceKind;
  /** @nullable */
  latitude?: number | null;
  /** @nullable */
  longitude?: number | null;
}

export type MonitorStatus = typeof MonitorStatus[keyof typeof MonitorStatus];


export const MonitorStatus = {
  UP: 'UP',
  DEGRADED: 'DEGRADED',
  CRITICAL: 'CRITICAL',
  DOWN: 'DOWN',
  MAINTENANCE: 'MAINTENANCE',
  UNKNOWN: 'UNKNOWN',
} as const;

export interface NetworkLink {
  id: string;
  name: string;
  link_type: LinkType;
  a_place: string;
  z_place: string;
  status: MonitorStatus;
  /** Active monitors on the link */
  monitors?: number;
  /** @nullable */
  provider?: string | null;
  /** @nullable */
  circuit_id?: string | null;
  /** @nullable */
  capacity_mbps?: number | null;
  /** @nullable */
  expected_rtt_ms?: number | null;
}

export interface NetworkMap {
  places: NetworkPlace[];
  links: NetworkLink[];
}

export type TopologyImportRequestFormat = typeof TopologyImportRequestFormat[keyof typeof TopologyImportRequestFormat];


export const TopologyImportRequestFormat = {
  yaml: 'yaml',
  csv: 'csv',
} as const;

export interface TopologyImportRequest {
  format: TopologyImportRequestFormat;
  /** @maxLength 5242880 */
  content: string;
  dry_run?: boolean;
}

export type TopologyImportItemKind = typeof TopologyImportItemKind[keyof typeof TopologyImportItemKind];


export const TopologyImportItemKind = {
  node: 'node',
  link: 'link',
} as const;

export type TopologyImportItemAction = typeof TopologyImportItemAction[keyof typeof TopologyImportItemAction];


export const TopologyImportItemAction = {
  create: 'create',
  update: 'update',
  unchanged: 'unchanged',
  error: 'error',
} as const;

export interface TopologyImportItem {
  kind: TopologyImportItemKind;
  ref: string;
  name: string;
  action: TopologyImportItemAction;
  /** Fields an update changes */
  changes?: string[];
  /** Why the record was rejected */
  message?: string;
  /** Line in the file (CSV: 1 is the header) */
  line?: number;
}

export interface TopologyImportResult {
  dry_run: boolean;
  /** True only when the records were written */
  applied: boolean;
  created: number;
  updated: number;
  unchanged: number;
  errors: number;
  items: TopologyImportItem[];
}

/**
 * Type-specific settings with defaults applied
 */
export type MonitorCheckConfig = { [key: string]: unknown };

export interface Monitor {
  id: string;
  name: string;
  /** @nullable */
  link_id?: string | null;
  /** @nullable */
  node_id?: string | null;
  check_type: CheckType;
  target_host: string;
  /** @nullable */
  target_port?: number | null;
  /** Type-specific settings with defaults applied */
  check_config: MonitorCheckConfig;
  interval_seconds: number;
  timeout_ms: number;
  warn_rtt_ms: number;
  crit_rtt_ms: number;
  warn_loss_pct: number;
  crit_loss_pct: number;
  breach_samples: number;
  recover_samples: number;
  /** @nullable */
  quorum?: number | null;
  cert_expiry_warn_days: number;
  labels: string[];
  is_active: boolean;
  /** @nullable */
  tenant_id?: string | null;
  probe_ids: string[];
  probe_labels: string[];
  created_at: string;
  updated_at: string;
}

export type MonitorInputCheckConfig = { [key: string]: unknown };

/**
 * Omitted thresholds default from the link's expected_rtt_ms when set (warning x1.2, critical x1.45, PRD §5.4), otherwise from the global defaults. For HTTP monitors target_host is derived from check_config.url. PATH monitors (a scheduled traceroute) default to a 300 s interval (minimum 60), a 1000 ms timeout per probe packet and loss thresholds of 34 % / 67 % (one or two of three packets).
 */
export interface MonitorInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @nullable */
  link_id?: string | null;
  /** @nullable */
  node_id?: string | null;
  check_type: CheckType;
  target_host?: string;
  /**
     * @minimum 1
     * @maximum 65535
     * @nullable
     */
  target_port?: number | null;
  check_config?: MonitorInputCheckConfig;
  /**
     * @minimum 10
     * @maximum 86400
     */
  interval_seconds?: number;
  /**
     * @minimum 100
     * @maximum 60000
     */
  timeout_ms?: number;
  /** @minimum 0 */
  warn_rtt_ms?: number;
  /** @minimum 0 */
  crit_rtt_ms?: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  warn_loss_pct?: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  crit_loss_pct?: number;
  /**
     * @minimum 1
     * @maximum 20
     */
  breach_samples?: number;
  /**
     * @minimum 1
     * @maximum 20
     */
  recover_samples?: number;
  /**
     * @minimum 1
     * @nullable
     */
  quorum?: number | null;
  /**
     * @minimum 0
     * @maximum 365
     */
  cert_expiry_warn_days?: number;
  labels?: string[];
  is_active?: boolean;
  /** @nullable */
  tenant_id?: string | null;
  probe_ids?: string[];
  /** Run from every active probe carrying one of these labels; "all" matches every probe. */
  probe_labels?: string[];
}

export type TelemetrySeriesResolution = typeof TelemetrySeriesResolution[keyof typeof TelemetrySeriesResolution];


export const TelemetrySeriesResolution = {
  raw: 'raw',
  '5m': '5m',
  '1h': '1h',
} as const;

export interface TelemetryPoint {
  time: string;
  probe_id: string;
  samples: number;
  failures: number;
  /** @nullable */
  rtt_avg_ms?: number | null;
  /** @nullable */
  rtt_min_ms?: number | null;
  /** @nullable */
  rtt_max_ms?: number | null;
  /** @nullable */
  jitter_ms?: number | null;
  /** @nullable */
  loss_pct?: number | null;
  /** @nullable */
  http_status?: number | null;
  /** @nullable */
  error?: string | null;
}

export interface TelemetrySeries {
  monitor_id: string;
  resolution: TelemetrySeriesResolution;
  points: TelemetryPoint[];
}

export interface EnrollmentTokenInput {
  /** @maxLength 500 */
  description?: string;
  /**
     * @minimum 1
     * @maximum 168
     */
  expires_in_hours?: number;
  /**
     * @minimum 1
     * @maximum 1000
     */
  max_uses?: number;
  /** @nullable */
  node_id?: string | null;
  labels?: string[];
  /** @nullable */
  tenant_id?: string | null;
  /**
     * Pre-fills --name in the generated install commands
     * @maxLength 100
     */
  probe_name?: string;
}

export type EnrollmentTokenStatus = typeof EnrollmentTokenStatus[keyof typeof EnrollmentTokenStatus];


export const EnrollmentTokenStatus = {
  ACTIVE: 'ACTIVE',
  EXHAUSTED: 'EXHAUSTED',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
} as const;

export interface EnrollmentToken {
  id: string;
  /** Public part of the token, for identification */
  token_id: string;
  /** @nullable */
  description?: string | null;
  /** @nullable */
  node_id?: string | null;
  labels: string[];
  /** @nullable */
  tenant_id?: string | null;
  max_uses: number;
  uses: number;
  expires_at: string;
  /** @nullable */
  revoked_at?: string | null;
  created_at: string;
  status: EnrollmentTokenStatus;
}

export interface InstallCommands {
  one_liner: string;
  env_form: string;
  inspect_first: string;
  docker: string;
  airgap_download_url: string;
  airgap_command: string;
}

export interface EnrollmentTokenCreated {
  /** The full secret token. Shown once; store it or use it now. */
  token: string;
  enrollment_token: EnrollmentToken;
  install: InstallCommands;
}

export type ProbeLifecycle = typeof ProbeLifecycle[keyof typeof ProbeLifecycle];


export const ProbeLifecycle = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
  DECOMMISSIONED: 'DECOMMISSIONED',
} as const;

export type ProbeStateStatus = typeof ProbeStateStatus[keyof typeof ProbeStateStatus];


export const ProbeStateStatus = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
} as const;

export interface ProbeState {
  status: ProbeStateStatus;
  status_since: string;
  /** @nullable */
  last_heartbeat?: string | null;
  /** @nullable */
  connected_at?: string | null;
  /** @nullable */
  last_ip?: string | null;
  applied_generation: number;
  target_generation: number;
  buffer_depth: number;
  dropped_samples: number;
  /** @nullable */
  clock_offset_ms?: number | null;
  /** |clock offset| > 2 s */
  clock_skewed?: boolean;
  /** @nullable */
  cpu_pct?: number | null;
  /** @nullable */
  mem_pct?: number | null;
}

export type ProbeHostFacts = { [key: string]: unknown };

export interface Probe {
  id: string;
  name: string;
  /** @nullable */
  node_id?: string | null;
  labels: string[];
  host_facts: ProbeHostFacts;
  version: string;
  /** Older than the recommended version */
  outdated: boolean;
  lifecycle: ProbeLifecycle;
  key_expires_at: string;
  /** @nullable */
  tenant_id?: string | null;
  monitor_count: number;
  created_at: string;
  state: ProbeState;
}

export interface ProbeInput {
  /**
     * @minLength 1
     * @maxLength 100
     */
  name: string;
  /** @nullable */
  node_id?: string | null;
  labels: string[];
}

export interface ProbeRolloutInput {
  /**
     * @minLength 1
     * @maxLength 41
     */
  version: string;
  /** Only these probes (default every active probe on an older version) */
  probe_ids?: string[];
}

export type ProbeRolloutStatus = typeof ProbeRolloutStatus[keyof typeof ProbeRolloutStatus];


export const ProbeRolloutStatus = {
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  HALTED: 'HALTED',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
} as const;

export type ProbeRolloutTargetState = typeof ProbeRolloutTargetState[keyof typeof ProbeRolloutTargetState];


export const ProbeRolloutTargetState = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  STAGED: 'STAGED',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  ROLLED_BACK: 'ROLLED_BACK',
  SKIPPED: 'SKIPPED',
} as const;

export interface ProbeRolloutTarget {
  probe_id: string;
  probe_name: string;
  from_version: string;
  state: ProbeRolloutTargetState;
  /** @nullable */
  detail: string | null;
  /** @nullable */
  sent_at: string | null;
  updated_at: string;
}

export interface ProbeRolloutCounts {
  pending: number;
  /** SENT or STAGED */
  in_flight: number;
  succeeded: number;
  /** FAILED or ROLLED_BACK */
  failed: number;
  skipped: number;
}

export interface ProbeRollout {
  id: string;
  version: string;
  status: ProbeRolloutStatus;
  /** Probes upgrading at once: 10 % of the targets, at least 1 */
  wave_size: number;
  /**
     * Why it halted
     * @nullable
     */
  reason: string | null;
  created_at: string;
  updated_at: string;
  /** @nullable */
  finished_at: string | null;
  counts: ProbeRolloutCounts;
  /** Only on a single rollout */
  targets?: ProbeRolloutTarget[];
}

export type ProbeReleaseArch = typeof ProbeReleaseArch[keyof typeof ProbeReleaseArch];


export const ProbeReleaseArch = {
  amd64: 'amd64',
  arm64: 'arm64',
} as const;

export interface ProbeRelease {
  version: string;
  arch: ProbeReleaseArch;
  sha256: string;
  size_bytes: number;
  created_at: string;
}

export interface ProbeReleaseList {
  /** @nullable */
  recommended_version?: string | null;
  releases: ProbeRelease[];
}

export interface ProbeMonitorState {
  probe_id: string;
  status: MonitorStatus;
  since: string;
  reason?: string;
  last_sample_at?: string;
}

export interface MonitorState {
  monitor_id: string;
  status: MonitorStatus;
  since: string;
  /** Why the state is bad (from the first agreeing probe) */
  reason?: string;
  agreement?: string;
  flapping: boolean;
  muted: boolean;
  probes: ProbeMonitorState[];
}

export interface BgpUpstream {
  asn: number;
  /** Share of the routes (0-1) */
  share: number;
  /** @nullable */
  first_significant?: string | null;
  /** @nullable */
  last_significant?: string | null;
}

export interface BgpAsn {
  id: string;
  asn: number;
  name: string;
  /** Full-table peer routes to its live prefixes */
  routes: number;
  upstreams: BgpUpstream[];
}

export interface BgpCount {
  asn: number;
  peers: number;
}

export interface BgpForeignOrigin {
  prefix: string;
  /** 0 for an AS_SET origin */
  asn: number;
  peers: number;
}

export type BgpRpkiStatus = typeof BgpRpkiStatus[keyof typeof BgpRpkiStatus];


export const BgpRpkiStatus = {
  valid: 'valid',
  invalid: 'invalid',
  'not-found': 'not-found',
} as const;

export interface BgpRpki {
  origin: number;
  status: BgpRpkiStatus;
  checked_at: string;
}

export interface BgpLinkedMonitor {
  id: string;
  name: string;
}

export interface BgpPrefix {
  id: string;
  prefix: string;
  own: boolean;
  /** @nullable */
  asn_id?: string | null;
  expected_origins: number[];
  /** An own prefix no longer announced */
  gone: boolean;
  /** @nullable */
  state_at?: string | null;
  full_peers: number;
  peers: number;
  /**
     * Share of full-table peers carrying it (0-1)
     * @nullable
     */
  visibility?: number | null;
  /**
     * The highest visibility within the last 7 days, which visibility is judged against
     * @nullable
     */
  usual_visibility?: number | null;
  origins: BgpCount[];
  foreign_origins: BgpForeignOrigin[];
  upstreams: BgpCount[];
  rpki: BgpRpki[];
  /**
     * The registry's name for the prefix's ASN (the own ASN, or an external prefix's first expected origin)
     * @nullable
     */
  asn_name?: string | null;
  labels: string[];
  /** PATH monitors the prefix is linked to */
  monitors: BgpLinkedMonitor[];
}

export interface BgpStatus {
  enabled: boolean;
  /** RIS Live connected */
  feed_up: boolean;
  /** bootstrapped since the feed connected */
  ready: boolean;
  /**
     * Connected since, or down since
     * @nullable
     */
  feed_since?: string | null;
  /** @nullable */
  last_message_at?: string | null;
  /** @nullable */
  bootstrapped_at?: string | null;
  full_peers_v4: number;
  full_peers_v6: number;
  /** Routes held for watched prefixes */
  routes: number;
  /** @nullable */
  last_error?: string | null;
  asns: BgpAsn[];
  prefixes: BgpPrefix[];
}

export interface BgpPrefixInput {
  prefix: string;
  /**
     * @minItems 1
     * @maxItems 10
     */
  expected_origins: number[];
  labels?: string[];
  /**
     * A PATH monitor to link it to
     * @nullable
     */
  monitor_id?: string | null;
}

export interface BgpPrefixUpdate {
  /**
     * @minItems 1
     * @maxItems 10
     */
  expected_origins: number[];
  labels?: string[];
}

export interface BgpPrefixConfig {
  id: string;
  prefix: string;
  own: boolean;
  expected_origins: number[];
  labels: string[];
  /** @nullable */
  asn_name?: string | null;
  monitors: BgpLinkedMonitor[];
}

export interface BgpWatchResult {
  prefixes: BgpPrefixConfig[];
  /** How many of them were not watched before */
  created: number;
}

export interface BgpPath {
  /** From the collector's peer to the origin, prepends merged */
  as_path: number[];
  peers: number;
  collectors: string[];
}

export interface BgpPaths {
  prefix: string;
  /** false: no data yet (its routes are loading, its fetch failed, or the feed is down), not no routes */
  ready: boolean;
  /** How many distinct paths there are; paths holds the most used 30 */
  distinct: number;
  /** Full-table peers of the prefix's address family */
  full_peers: number;
  /** Full-table peers with a route to the prefix */
  peers: number;
  /** Over all those routes: the AS just before the origin, which hands the traffic to it (most used first; routes from the origin's own sessions are left out) */
  handoffs: BgpCount[];
  paths: BgpPath[];
}

/**
 * Announcement or withdrawal
 */
export type BgpUpdateKind = typeof BgpUpdateKind[keyof typeof BgpUpdateKind];


export const BgpUpdateKind = {
  A: 'A',
  W: 'W',
} as const;

export interface BgpUpdate {
  at: string;
  prefix: string;
  /** RIS collector number (rrcNN) */
  collector: string;
  peer: string;
  peer_asn: number;
  /** Announcement or withdrawal */
  kind: BgpUpdateKind;
  /** @nullable */
  as_path?: number[] | null;
}

export interface PathAS {
  asn: number;
  name: string;
  country?: string;
}

/**
 * UNSUPPORTED: the probe cannot run PATH checks. LEARNING: no baseline yet. CHANGED: the path left its baseline. VIOLATION: it breaks target_asns or avoid_asns.
 */
export type MonitorPathState = typeof MonitorPathState[keyof typeof MonitorPathState];


export const MonitorPathState = {
  UNSUPPORTED: 'UNSUPPORTED',
  NO_DATA: 'NO_DATA',
  LEARNING: 'LEARNING',
  ON_BASELINE: 'ON_BASELINE',
  CHANGED: 'CHANGED',
  VIOLATION: 'VIOLATION',
} as const;

/**
 * The latest labelled trace, shaped like a TRACEROUTE ad-hoc result
 * @nullable
 */
export type MonitorPathTrace = { [key: string]: unknown } | null;

export interface MonitorPath {
  probe_id: string;
  probe_name: string;
  /** UNSUPPORTED: the probe cannot run PATH checks. LEARNING: no baseline yet. CHANGED: the path left its baseline. VIOLATION: it breaks target_asns or avoid_asns. */
  state: MonitorPathState;
  /** @nullable */
  baseline?: PathAS[] | null;
  /** @nullable */
  baseline_since?: string | null;
  /** @nullable */
  current?: PathAS[] | null;
  /** @nullable */
  current_since?: string | null;
  /**
     * The latest labelled trace, shaped like a TRACEROUTE ad-hoc result
     * @nullable
     */
  trace?: MonitorPathTrace;
  /** @nullable */
  last_sample_at?: string | null;
  /** @nullable */
  deviation_since?: string | null;
  /** @nullable */
  reason?: string | null;
}

export type PathChangeEvent = typeof PathChangeEvent[keyof typeof PathChangeEvent];


export const PathChangeEvent = {
  BASELINE: 'BASELINE',
  CHANGED: 'CHANGED',
  RESTORED: 'RESTORED',
  ACCEPTED: 'ACCEPTED',
  RESET: 'RESET',
} as const;

/**
 * @nullable
 */
export type PathChangeTrace = { [key: string]: unknown } | null;

export interface PathChange {
  at: string;
  probe_id: string;
  event: PathChangeEvent;
  as_path: PathAS[];
  /** @nullable */
  prev_as_path?: PathAS[] | null;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  actor_id?: string | null;
  /** @nullable */
  trace?: PathChangeTrace;
}

export interface StatusChange {
  at: string;
  /**
     * null = effective state
     * @nullable
     */
  probe_id?: string | null;
  status: MonitorStatus;
  /** @nullable */
  prev_status?: string | null;
  /** @nullable */
  reason?: string | null;
}

export interface MuteInput {
  /**
     * @minimum 1
     * @maximum 10080
     */
  duration_minutes: number;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason: string;
}

export interface Mute {
  id: string;
  monitor_id: string;
  reason: string;
  until: string;
  /** @nullable */
  created_by?: string | null;
  created_at: string;
}

export type IncidentKind = typeof IncidentKind[keyof typeof IncidentKind];


export const IncidentKind = {
  MONITOR: 'MONITOR',
  PROBE_OFFLINE: 'PROBE_OFFLINE',
  PROBE_PATH_FAILURE: 'PROBE_PATH_FAILURE',
  FLAPPING: 'FLAPPING',
  PATH_CHANGE: 'PATH_CHANGE',
  BGP_ORIGIN: 'BGP_ORIGIN',
  BGP_VISIBILITY: 'BGP_VISIBILITY',
  BGP_UPSTREAM: 'BGP_UPSTREAM',
  BGP_RPKI: 'BGP_RPKI',
} as const;

export type IncidentSeverity = typeof IncidentSeverity[keyof typeof IncidentSeverity];


export const IncidentSeverity = {
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL',
  DOWN: 'DOWN',
} as const;

export type IncidentStatus = typeof IncidentStatus[keyof typeof IncidentStatus];


export const IncidentStatus = {
  OPEN: 'OPEN',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  RESOLVED: 'RESOLVED',
} as const;

export interface Incident {
  id: string;
  /** @nullable */
  monitor_id?: string | null;
  /** @nullable */
  probe_id?: string | null;
  /** @nullable */
  bgp_prefix_id?: string | null;
  /** @nullable */
  bgp_asn_id?: string | null;
  kind: IncidentKind;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  /** @nullable */
  reason?: string | null;
  /** @nullable */
  suppressed_by?: string | null;
  started_at: string;
  /** @nullable */
  acknowledged_by?: string | null;
  /** @nullable */
  acknowledged_at?: string | null;
  /** @nullable */
  resolved_at?: string | null;
  escalated_tier: number;
  /** @nullable */
  tenant_id?: string | null;
}

/**
 * @nullable
 */
export type IncidentEventBody = { [key: string]: unknown } | null;

export interface IncidentEvent {
  id: number;
  at: string;
  /** @nullable */
  actor_id?: string | null;
  /** @nullable */
  actor_name?: string | null;
  event: string;
  /** @nullable */
  body?: IncidentEventBody;
}

export type IncidentDeliveryStatus = typeof IncidentDeliveryStatus[keyof typeof IncidentDeliveryStatus];


export const IncidentDeliveryStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
} as const;

export interface IncidentDelivery {
  id: number;
  channel_id: string;
  channel_name: string;
  channel_kind: string;
  event: string;
  status: IncidentDeliveryStatus;
  attempts: number;
  /** @nullable */
  last_error?: string | null;
  created_at: string;
  /** @nullable */
  sent_at?: string | null;
}

export interface IncidentDetail {
  incident: Incident;
  events: IncidentEvent[];
  deliveries: IncidentDelivery[];
}

export interface AckInput {
  /** @maxLength 4000 */
  note?: string;
}

export interface NoteInput {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  body: string;
}

export type MaintenanceTargetType = typeof MaintenanceTargetType[keyof typeof MaintenanceTargetType];


export const MaintenanceTargetType = {
  MONITOR: 'MONITOR',
  LINK: 'LINK',
  NODE: 'NODE',
  PROBE: 'PROBE',
  BGP_PREFIX: 'BGP_PREFIX',
  BGP_ASN: 'BGP_ASN',
} as const;

export type MaintenanceWindowState = typeof MaintenanceWindowState[keyof typeof MaintenanceWindowState];


export const MaintenanceWindowState = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  ENDED: 'ENDED',
  PAUSED: 'PAUSED',
} as const;

export interface MaintenanceWindow {
  id: string;
  title: string;
  /** @nullable */
  description?: string | null;
  target_type: MaintenanceTargetType;
  target_id: string;
  /** @nullable */
  target_name?: string | null;
  include_subtree: boolean;
  starts_at: string;
  ends_at: string;
  /** @nullable */
  rrule?: string | null;
  timezone: string;
  paused: boolean;
  state: MaintenanceWindowState;
  /** @nullable */
  next_start?: string | null;
  /** @nullable */
  next_end?: string | null;
  created_at: string;
  updated_at: string;
}

export type MaintenanceWindowInputPreset = typeof MaintenanceWindowInputPreset[keyof typeof MaintenanceWindowInputPreset];


export const MaintenanceWindowInputPreset = {
  once: 'once',
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
  custom: 'custom',
} as const;

export type MaintenanceWindowInputWeekdaysItem = typeof MaintenanceWindowInputWeekdaysItem[keyof typeof MaintenanceWindowInputWeekdaysItem];


export const MaintenanceWindowInputWeekdaysItem = {
  MO: 'MO',
  TU: 'TU',
  WE: 'WE',
  TH: 'TH',
  FR: 'FR',
  SA: 'SA',
  SU: 'SU',
} as const;

export interface MaintenanceWindowInput {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /** @nullable */
  description?: string | null;
  target_type: MaintenanceTargetType;
  target_id: string;
  include_subtree?: boolean;
  /** Start of the (first) occurrence */
  starts_at: string;
  /** End of the (first) occurrence */
  ends_at: string;
  preset?: MaintenanceWindowInputPreset;
  weekdays?: MaintenanceWindowInputWeekdaysItem[];
  /**
     * @minimum 1
     * @maximum 31
     */
  month_day?: number;
  /** RFC 5545 RRULE (preset custom) */
  rrule?: string;
  timezone?: string;
  paused?: boolean;
}

export type ChannelKind = typeof ChannelKind[keyof typeof ChannelKind];


export const ChannelKind = {
  WEBHOOK: 'WEBHOOK',
  TELEGRAM: 'TELEGRAM',
  SLACK: 'SLACK',
  EMAIL: 'EMAIL',
} as const;

/**
 * Non-secret settings
 */
export type NotificationChannelConfig = { [key: string]: unknown };

export interface NotificationChannel {
  id: string;
  kind: ChannelKind;
  name: string;
  enabled: boolean;
  /** @nullable */
  tenant_id?: string | null;
  /** Non-secret settings */
  config: NotificationChannelConfig;
  /** Which secret settings hold a value (values are never returned) */
  secrets_set: string[];
  created_at: string;
  updated_at: string;
}

/**
 * WEBHOOK url, method, content_type, headers*, hmac_key*; TELEGRAM chat_id, thread_id, silent, protect_content, api_url, bot_token*; SLACK webhook_url*, channel, bot_name, icon_emoji; EMAIL host, port, tls_mode, verify_tls, username, password*, from, to, cc, bcc; all: title_template, body_template. (* secret)
 */
export type NotificationChannelInputConfig = { [key: string]: unknown };

export interface NotificationChannelInput {
  kind: ChannelKind;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  enabled?: boolean;
  /** @nullable */
  tenant_id?: string | null;
  /** WEBHOOK url, method, content_type, headers*, hmac_key*; TELEGRAM chat_id, thread_id, silent, protect_content, api_url, bot_token*; SLACK webhook_url*, channel, bot_name, icon_emoji; EMAIL host, port, tls_mode, verify_tls, username, password*, from, to, cc, bcc; all: title_template, body_template. (* secret) */
  config: NotificationChannelInputConfig;
}

export interface TestResult {
  ok: boolean;
  error?: string;
}

export interface RouteMatch {
  node_ids?: string[];
  labels?: string[];
  tenant_ids?: string[];
  kinds?: IncidentKind[];
}

export interface NotificationRoute {
  id: string;
  channel_id: string;
  min_severity: IncidentSeverity;
  match: RouteMatch;
  tier: number;
  /** @nullable */
  escalate_after_min?: number | null;
  enabled: boolean;
  created_at: string;
}

export interface NotificationRouteInput {
  channel_id: string;
  min_severity?: IncidentSeverity;
  match?: RouteMatch;
  /**
     * @minimum 1
     * @maximum 5
     */
  tier?: number;
  /**
     * @minimum 1
     * @maximum 1440
     * @nullable
     */
  escalate_after_min?: number | null;
  enabled?: boolean;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  /** @nullable */
  tenant_id?: string | null;
  is_active: boolean;
  /** @nullable */
  last_login_at?: string | null;
  created_at: string;
}

export interface UserCreate {
  email: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  display_name: string;
  role: Role;
  /** @nullable */
  tenant_id?: string | null;
  /**
     * @minLength 12
     * @maxLength 200
     */
  password: string;
}

export interface UserUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  display_name: string;
  role: Role;
  /** @nullable */
  tenant_id?: string | null;
  is_active: boolean;
  /**
     * @minLength 12
     * @maxLength 200
     */
  password?: string;
}

export interface ThresholdSettings {
  /** @minimum 0 */
  warn_rtt_ms: number;
  /** @minimum 0 */
  crit_rtt_ms: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  warn_loss_pct: number;
  /**
     * @minimum 0
     * @maximum 100
     */
  crit_loss_pct: number;
}

/**
 * @nullable
 */
export type AuditEntryDiff = { [key: string]: unknown } | null;

export interface AuditEntry {
  id: number;
  at: string;
  /** @nullable */
  actor_id?: string | null;
  /** @nullable */
  actor_name?: string | null;
  /** @nullable */
  actor_email?: string | null;
  /** @nullable */
  ip?: string | null;
  action: string;
  entity: string;
  /** @nullable */
  entity_id?: string | null;
  /** @nullable */
  diff?: AuditEntryDiff;
}

export type AdhocKind = typeof AdhocKind[keyof typeof AdhocKind];


export const AdhocKind = {
  ICMP: 'ICMP',
  TCP: 'TCP',
  HTTP: 'HTTP',
  TRACEROUTE: 'TRACEROUTE',
} as const;

export interface AdhocInput {
  kind: AdhocKind;
  /** Host/IP, or a URL for HTTP */
  target: string;
  /**
     * @minimum 1
     * @maximum 65535
     */
  port?: number;
  /**
     * @minimum 1
     * @maximum 50
     */
  count?: number;
  /**
     * @minimum 1
     * @maximum 64
     */
  max_hops?: number;
  monitor_id?: string;
}

export type AdhocRequestParams = { [key: string]: unknown };

export type AdhocRequestStatus = typeof AdhocRequestStatus[keyof typeof AdhocRequestStatus];


export const AdhocRequestStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  DONE: 'DONE',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
} as const;

/**
 * @nullable
 */
export type AdhocRequestResult = { [key: string]: unknown } | null;

export interface AdhocRequest {
  id: string;
  probe_id: string;
  /** @nullable */
  monitor_id?: string | null;
  kind: AdhocKind;
  params: AdhocRequestParams;
  status: AdhocRequestStatus;
  /** @nullable */
  result?: AdhocRequestResult;
  /** @nullable */
  error?: string | null;
  created_at: string;
  /** @nullable */
  completed_at?: string | null;
}

export type StreamMessageType = typeof StreamMessageType[keyof typeof StreamMessageType];


export const StreamMessageType = {
  snapshot: 'snapshot',
  monitor_state: 'monitor_state',
  probe_state: 'probe_state',
  incident: 'incident',
  adhoc_result: 'adhoc_result',
  heartbeat: 'heartbeat',
} as const;

/**
 * One JSON message on the /api/ws stream
 */
export interface StreamMessage {
  type: StreamMessageType;
  at: string;
  monitors?: MonitorState[];
  monitor?: MonitorState;
  probes?: Probe[];
  probe?: Probe;
  incidents?: Incident[];
  incident?: Incident;
  adhoc?: AdhocRequest;
}

/**
 * Not signed in
 */
export type UnauthorizedResponse = Error;

/**
 * Not found, or not in the caller's scope
 */
export type NotFoundResponse = Error;

/**
 * Conflicts with existing data
 */
export type ConflictResponse = Error;

/**
 * Validation failed
 */
export type UnprocessableResponse = Error;

/**
 * Rate limited
 */
export type TooManyRequestsResponse = Error;

/**
 * Calendar month, YYYY-MM
 */
export type SlaMonthParameter = string;

/**
 * IANA time zone for the month's boundaries
 */
export type SlaTimeZoneParameter = string;

/**
 * Admins and NOC only; customers always get their own tenant
 */
export type SlaTenantParameter = string;

export type ListNodesParams = {
parent_id?: string;
type?: NodeType;
};

export type DeleteNodeParams = {
/**
 * Also delete the nodes inside it
 */
recursive?: boolean;
};

export type ListLinksParams = {
/**
 * Links with this node as A or Z end
 */
node_id?: string;
};

export type ListMonitorsParams = {
link_id?: string;
node_id?: string;
};

export type GetMonitorTelemetryParams = {
from: string;
to?: string;
/**
 * raw samples, or 5m / 1h rollups. Defaults by range.
 */
resolution?: GetMonitorTelemetryResolution;
probe_id?: string;
};

export type GetMonitorTelemetryResolution = typeof GetMonitorTelemetryResolution[keyof typeof GetMonitorTelemetryResolution];


export const GetMonitorTelemetryResolution = {
  raw: 'raw',
  '5m': '5m',
  '1h': '1h',
} as const;

export type ListProbesParams = {
/**
 * Include REVOKED and DECOMMISSIONED probes
 */
include_retired?: boolean;
};

export type ListMonitorStatusChangesParams = {
since?: string;
effective_only?: boolean;
};

export type ListMonitorPathChangesParams = {
since?: string;
};

export type ListIncidentsParams = {
include_resolved?: boolean;
monitor_id?: string;
probe_id?: string;
/**
 * Resolved incidents are included back to this time (default 7 days)
 */
since?: string;
/**
 * @minimum 1
 * @maximum 1000
 */
limit?: number;
};

export type ListAuditLogParams = {
entity?: string;
actor_id?: string;
/**
 * Page backwards from this id
 */
before_id?: number;
/**
 * @minimum 1
 * @maximum 500
 */
limit?: number;
};

export type ListBgpUpdatesParams = {
since?: string;
};

export type GetSlaReportParams = {
/**
 * Calendar month, YYYY-MM
 * @pattern ^[0-9]{4}-(0[1-9]|1[0-2])$
 */
month: SlaMonthParameter;
/**
 * IANA time zone for the month's boundaries
 */
tz?: SlaTimeZoneParameter;
/**
 * Admins and NOC only; customers always get their own tenant
 */
tenant_id?: SlaTenantParameter;
};

export type ExportSlaReportParams = {
/**
 * Calendar month, YYYY-MM
 * @pattern ^[0-9]{4}-(0[1-9]|1[0-2])$
 */
month: SlaMonthParameter;
/**
 * IANA time zone for the month's boundaries
 */
tz?: SlaTimeZoneParameter;
/**
 * Admins and NOC only; customers always get their own tenant
 */
tenant_id?: SlaTenantParameter;
format: ExportSlaReportFormat;
};

export type ExportSlaReportFormat = typeof ExportSlaReportFormat[keyof typeof ExportSlaReportFormat];


export const ExportSlaReportFormat = {
  csv: 'csv',
  pdf: 'pdf',
} as const;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getLoginUrl = () => {




  return `/api/v1/auth/login`
}

/**
 * @summary Log in with email and password
 */
export const login = async (loginRequest: LoginRequest, options?: Parameters<typeof apiFetch>[1]): Promise<Me> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Me>(getLoginUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(loginRequest)
  }
);}





export const getLoginMutationKey = () => ['login'] as const;

export const getLoginMutationOptions = <TError = UnauthorizedResponse | TooManyRequestsResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext> => {

const mutationKey = getLoginMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof login>>, LoginMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  login(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LoginMutationResult = NonNullable<Awaited<ReturnType<typeof login>>>
    export type LoginMutationBody = LoginRequest
    export type LoginMutationError = UnauthorizedResponse | TooManyRequestsResponse
    export type LoginMutationVariables = {data: LoginRequest}

    /**
 * @summary Log in with email and password
 */
export const useLogin = <TError = UnauthorizedResponse | TooManyRequestsResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof login>>, TError,LoginMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof login>>,
        TError,
        LoginMutationVariables,
        TContext
      > => {
      return useMutation(getLoginMutationOptions(options), queryClient);
    }

export const getLogoutUrl = () => {




  return `/api/v1/auth/logout`
}

/**
 * @summary End the current session
 */
export const logout = async ( options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getLogoutUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getLogoutMutationKey = () => ['logout'] as const;

export const getLogoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext> => {

const mutationKey = getLogoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof logout>>, void> = () => {


          return  logout(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type LogoutMutationResult = NonNullable<Awaited<ReturnType<typeof logout>>>

    export type LogoutMutationError = unknown


    /**
 * @summary End the current session
 */
export const useLogout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError,void, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof logout>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getLogoutMutationOptions(options), queryClient);
    }

export const getGetMeUrl = () => {




  return `/api/v1/auth/me`
}

/**
 * @summary The signed-in user
 */
export const getMe = async ( options?: Parameters<typeof apiFetch>[1]): Promise<Me> => {

  return apiFetch<Me>(getGetMeUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMeQueryKey = () => {
    return [
    `/api/v1/auth/me`
    ] as const;
    }


export const getGetMeQueryOptions = <TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMeQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMe>>> = ({ signal }) => getMe({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMeQueryResult = NonNullable<Awaited<ReturnType<typeof getMe>>>
export type GetMeQueryError = UnauthorizedResponse


export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The signed-in user
 */

export function useGetMe<TData = Awaited<ReturnType<typeof getMe>>, TError = UnauthorizedResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMeQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListTenantsUrl = () => {




  return `/api/v1/tenants`
}

export const listTenants = async ( options?: Parameters<typeof apiFetch>[1]): Promise<Tenant[]> => {

  return apiFetch<Tenant[]>(getListTenantsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantsQueryKey = () => {
    return [
    `/api/v1/tenants`
    ] as const;
    }


export const getListTenantsQueryOptions = <TData = Awaited<ReturnType<typeof listTenants>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenants>>> = ({ signal }) => listTenants({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTenantsQueryResult = NonNullable<Awaited<ReturnType<typeof listTenants>>>
export type ListTenantsQueryError = unknown


export function useListTenants<TData = Awaited<ReturnType<typeof listTenants>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenants>>,
          TError,
          Awaited<ReturnType<typeof listTenants>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenants<TData = Awaited<ReturnType<typeof listTenants>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenants>>,
          TError,
          Awaited<ReturnType<typeof listTenants>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenants<TData = Awaited<ReturnType<typeof listTenants>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListTenants<TData = Awaited<ReturnType<typeof listTenants>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenants>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTenantsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantUrl = () => {




  return `/api/v1/tenants`
}

export const createTenant = async (tenantInput: TenantInput, options?: Parameters<typeof apiFetch>[1]): Promise<Tenant> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Tenant>(getCreateTenantUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenantInput)
  }
);}





export const getCreateTenantMutationKey = () => ['createTenant'] as const;

export const getCreateTenantMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenant>>, TError,CreateTenantMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenant>>, TError,CreateTenantMutationVariables, TContext> => {

const mutationKey = getCreateTenantMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenant>>, CreateTenantMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createTenant(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantMutationResult = NonNullable<Awaited<ReturnType<typeof createTenant>>>
    export type CreateTenantMutationBody = TenantInput
    export type CreateTenantMutationError = ConflictResponse | UnprocessableResponse
    export type CreateTenantMutationVariables = {data: TenantInput}

    export const useCreateTenant = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenant>>, TError,CreateTenantMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTenant>>,
        TError,
        CreateTenantMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantMutationOptions(options), queryClient);
    }

export const getListNodesUrl = (params?: ListNodesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/nodes?${stringifiedParams}` : `/api/v1/nodes`
}

export const listNodes = async (params?: ListNodesParams, options?: Parameters<typeof apiFetch>[1]): Promise<Node[]> => {

  return apiFetch<Node[]>(getListNodesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNodesQueryKey = (params?: ListNodesParams,) => {
    return [
    `/api/v1/nodes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNodesQueryOptions = <TData = Awaited<ReturnType<typeof listNodes>>, TError = unknown>(params?: ListNodesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNodesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNodes>>> = ({ signal }) => listNodes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNodesQueryResult = NonNullable<Awaited<ReturnType<typeof listNodes>>>
export type ListNodesQueryError = unknown


export function useListNodes<TData = Awaited<ReturnType<typeof listNodes>>, TError = unknown>(
 params: undefined |  ListNodesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNodes>>,
          TError,
          Awaited<ReturnType<typeof listNodes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNodes<TData = Awaited<ReturnType<typeof listNodes>>, TError = unknown>(
 params?: ListNodesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNodes>>,
          TError,
          Awaited<ReturnType<typeof listNodes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNodes<TData = Awaited<ReturnType<typeof listNodes>>, TError = unknown>(
 params?: ListNodesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListNodes<TData = Awaited<ReturnType<typeof listNodes>>, TError = unknown>(
 params?: ListNodesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNodes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNodesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateNodeUrl = () => {




  return `/api/v1/nodes`
}

export const createNode = async (nodeInput: NodeInput, options?: Parameters<typeof apiFetch>[1]): Promise<Node> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Node>(getCreateNodeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nodeInput)
  }
);}





export const getCreateNodeMutationKey = () => ['createNode'] as const;

export const getCreateNodeMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNode>>, TError,CreateNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createNode>>, TError,CreateNodeMutationVariables, TContext> => {

const mutationKey = getCreateNodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createNode>>, CreateNodeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createNode(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateNodeMutationResult = NonNullable<Awaited<ReturnType<typeof createNode>>>
    export type CreateNodeMutationBody = NodeInput
    export type CreateNodeMutationError = ConflictResponse | UnprocessableResponse
    export type CreateNodeMutationVariables = {data: NodeInput}

    export const useCreateNode = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNode>>, TError,CreateNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createNode>>,
        TError,
        CreateNodeMutationVariables,
        TContext
      > => {
      return useMutation(getCreateNodeMutationOptions(options), queryClient);
    }

export const getGetNodeUrl = (id: string,) => {




  return `/api/v1/nodes/${id}`
}

export const getNode = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Node> => {

  return apiFetch<Node>(getGetNodeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNodeQueryKey = (id: string,) => {
    return [
    `/api/v1/nodes/${id}`
    ] as const;
    }


export const getGetNodeQueryOptions = <TData = Awaited<ReturnType<typeof getNode>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNodeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNode>>> = ({ signal }) => getNode(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNodeQueryResult = NonNullable<Awaited<ReturnType<typeof getNode>>>
export type GetNodeQueryError = NotFoundResponse


export function useGetNode<TData = Awaited<ReturnType<typeof getNode>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNode>>,
          TError,
          Awaited<ReturnType<typeof getNode>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNode<TData = Awaited<ReturnType<typeof getNode>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNode>>,
          TError,
          Awaited<ReturnType<typeof getNode>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNode<TData = Awaited<ReturnType<typeof getNode>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetNode<TData = Awaited<ReturnType<typeof getNode>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNode>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNodeQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateNodeUrl = (id: string,) => {




  return `/api/v1/nodes/${id}`
}

export const updateNode = async (id: string,
    nodeInput: NodeInput, options?: Parameters<typeof apiFetch>[1]): Promise<Node> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Node>(getUpdateNodeUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(nodeInput)
  }
);}





export const getUpdateNodeMutationKey = () => ['updateNode'] as const;

export const getUpdateNodeMutationOptions = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNode>>, TError,UpdateNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNode>>, TError,UpdateNodeMutationVariables, TContext> => {

const mutationKey = getUpdateNodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNode>>, UpdateNodeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateNode(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNodeMutationResult = NonNullable<Awaited<ReturnType<typeof updateNode>>>
    export type UpdateNodeMutationBody = NodeInput
    export type UpdateNodeMutationError = NotFoundResponse | ConflictResponse | UnprocessableResponse
    export type UpdateNodeMutationVariables = {id: string;data: NodeInput}

    export const useUpdateNode = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNode>>, TError,UpdateNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNode>>,
        TError,
        UpdateNodeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateNodeMutationOptions(options), queryClient);
    }

export const getDeleteNodeUrl = (id: string,
    params?: DeleteNodeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/nodes/${id}?${stringifiedParams}` : `/api/v1/nodes/${id}`
}

/**
 * Deletes the node with its subtree, the links touching them and their monitors. A node with other nodes inside it is deleted only with recursive=true; otherwise the answer is 409 node_has_children, whose message says how many would go with it.
 */
export const deleteNode = async (id: string,
    params?: DeleteNodeParams, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteNodeUrl(id,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteNodeMutationKey = () => ['deleteNode'] as const;

export const getDeleteNodeMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNode>>, TError,DeleteNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNode>>, TError,DeleteNodeMutationVariables, TContext> => {

const mutationKey = getDeleteNodeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNode>>, DeleteNodeMutationVariables> = (props) => {
          const {id,params} = props ?? {};

          return  deleteNode(id,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNodeMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNode>>>

    export type DeleteNodeMutationError = NotFoundResponse | ConflictResponse
    export type DeleteNodeMutationVariables = {id: string;params?: DeleteNodeParams}

    export const useDeleteNode = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNode>>, TError,DeleteNodeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNode>>,
        TError,
        DeleteNodeMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteNodeMutationOptions(options), queryClient);
    }

export const getListLinksUrl = (params?: ListLinksParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/links?${stringifiedParams}` : `/api/v1/links`
}

export const listLinks = async (params?: ListLinksParams, options?: Parameters<typeof apiFetch>[1]): Promise<Link[]> => {

  return apiFetch<Link[]>(getListLinksUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListLinksQueryKey = (params?: ListLinksParams,) => {
    return [
    `/api/v1/links`, ...(params ? [params] : [])
    ] as const;
    }


export const getListLinksQueryOptions = <TData = Awaited<ReturnType<typeof listLinks>>, TError = unknown>(params?: ListLinksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListLinksQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listLinks>>> = ({ signal }) => listLinks(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListLinksQueryResult = NonNullable<Awaited<ReturnType<typeof listLinks>>>
export type ListLinksQueryError = unknown


export function useListLinks<TData = Awaited<ReturnType<typeof listLinks>>, TError = unknown>(
 params: undefined |  ListLinksParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLinks>>,
          TError,
          Awaited<ReturnType<typeof listLinks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLinks<TData = Awaited<ReturnType<typeof listLinks>>, TError = unknown>(
 params?: ListLinksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listLinks>>,
          TError,
          Awaited<ReturnType<typeof listLinks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListLinks<TData = Awaited<ReturnType<typeof listLinks>>, TError = unknown>(
 params?: ListLinksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListLinks<TData = Awaited<ReturnType<typeof listLinks>>, TError = unknown>(
 params?: ListLinksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listLinks>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListLinksQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateLinkUrl = () => {




  return `/api/v1/links`
}

export const createLink = async (linkInput: LinkInput, options?: Parameters<typeof apiFetch>[1]): Promise<Link> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Link>(getCreateLinkUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(linkInput)
  }
);}





export const getCreateLinkMutationKey = () => ['createLink'] as const;

export const getCreateLinkMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLink>>, TError,CreateLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createLink>>, TError,CreateLinkMutationVariables, TContext> => {

const mutationKey = getCreateLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createLink>>, CreateLinkMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createLink(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateLinkMutationResult = NonNullable<Awaited<ReturnType<typeof createLink>>>
    export type CreateLinkMutationBody = LinkInput
    export type CreateLinkMutationError = ConflictResponse | UnprocessableResponse
    export type CreateLinkMutationVariables = {data: LinkInput}

    export const useCreateLink = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createLink>>, TError,CreateLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createLink>>,
        TError,
        CreateLinkMutationVariables,
        TContext
      > => {
      return useMutation(getCreateLinkMutationOptions(options), queryClient);
    }

export const getGetLinkUrl = (id: string,) => {




  return `/api/v1/links/${id}`
}

export const getLink = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Link> => {

  return apiFetch<Link>(getGetLinkUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetLinkQueryKey = (id: string,) => {
    return [
    `/api/v1/links/${id}`
    ] as const;
    }


export const getGetLinkQueryOptions = <TData = Awaited<ReturnType<typeof getLink>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetLinkQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getLink>>> = ({ signal }) => getLink(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetLinkQueryResult = NonNullable<Awaited<ReturnType<typeof getLink>>>
export type GetLinkQueryError = NotFoundResponse


export function useGetLink<TData = Awaited<ReturnType<typeof getLink>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLink>>,
          TError,
          Awaited<ReturnType<typeof getLink>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetLink<TData = Awaited<ReturnType<typeof getLink>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLink>>,
          TError,
          Awaited<ReturnType<typeof getLink>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetLink<TData = Awaited<ReturnType<typeof getLink>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetLink<TData = Awaited<ReturnType<typeof getLink>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getLink>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetLinkQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateLinkUrl = (id: string,) => {




  return `/api/v1/links/${id}`
}

export const updateLink = async (id: string,
    linkInput: LinkInput, options?: Parameters<typeof apiFetch>[1]): Promise<Link> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Link>(getUpdateLinkUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(linkInput)
  }
);}





export const getUpdateLinkMutationKey = () => ['updateLink'] as const;

export const getUpdateLinkMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLink>>, TError,UpdateLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateLink>>, TError,UpdateLinkMutationVariables, TContext> => {

const mutationKey = getUpdateLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateLink>>, UpdateLinkMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateLink(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateLinkMutationResult = NonNullable<Awaited<ReturnType<typeof updateLink>>>
    export type UpdateLinkMutationBody = LinkInput
    export type UpdateLinkMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateLinkMutationVariables = {id: string;data: LinkInput}

    export const useUpdateLink = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateLink>>, TError,UpdateLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateLink>>,
        TError,
        UpdateLinkMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateLinkMutationOptions(options), queryClient);
    }

export const getDeleteLinkUrl = (id: string,) => {




  return `/api/v1/links/${id}`
}

export const deleteLink = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteLinkUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteLinkMutationKey = () => ['deleteLink'] as const;

export const getDeleteLinkMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLink>>, TError,DeleteLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteLink>>, TError,DeleteLinkMutationVariables, TContext> => {

const mutationKey = getDeleteLinkMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteLink>>, DeleteLinkMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteLink(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteLinkMutationResult = NonNullable<Awaited<ReturnType<typeof deleteLink>>>

    export type DeleteLinkMutationError = NotFoundResponse
    export type DeleteLinkMutationVariables = {id: string}

    export const useDeleteLink = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteLink>>, TError,DeleteLinkMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteLink>>,
        TError,
        DeleteLinkMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteLinkMutationOptions(options), queryClient);
    }

export const getGetNetworkUrl = () => {




  return `/api/v1/network`
}

/**
 * Links in the caller's scope with their health. For a customer, an end node the customer's tenant owns is shown as itself; any other end node collapses into an anonymous "Atlas network" place in its city (docs/adr/2026-09-28-customer-tenancy.md). Internal users see the real end nodes.
 * @summary The caller's links and their end places (the customer network map)
 */
export const getNetwork = async ( options?: Parameters<typeof apiFetch>[1]): Promise<NetworkMap> => {

  return apiFetch<NetworkMap>(getGetNetworkUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNetworkQueryKey = () => {
    return [
    `/api/v1/network`
    ] as const;
    }


export const getGetNetworkQueryOptions = <TData = Awaited<ReturnType<typeof getNetwork>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNetworkQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNetwork>>> = ({ signal }) => getNetwork({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNetworkQueryResult = NonNullable<Awaited<ReturnType<typeof getNetwork>>>
export type GetNetworkQueryError = unknown


export function useGetNetwork<TData = Awaited<ReturnType<typeof getNetwork>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNetwork>>,
          TError,
          Awaited<ReturnType<typeof getNetwork>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNetwork<TData = Awaited<ReturnType<typeof getNetwork>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNetwork>>,
          TError,
          Awaited<ReturnType<typeof getNetwork>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNetwork<TData = Awaited<ReturnType<typeof getNetwork>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's links and their end places (the customer network map)
 */

export function useGetNetwork<TData = Awaited<ReturnType<typeof getNetwork>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNetwork>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNetworkQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getImportTopologyUrl = () => {




  return `/api/v1/topology/import`
}

/**
 * Records are keyed by `ref` (stored as external_ref); parents, link ends and depends_on refer to refs in the file or already in Atlas. Name, type, parent (empty means top level) and link ends are always applied; other fields only when present, so a re-import keeps values set in the UI. The whole file runs in one transaction and is applied only when every record is valid; dry_run (the default) runs the same transaction and rolls it back. See docs/adr/2026-09-28-3d-topology.md.
 * @summary Import nodes and links from YAML or CSV (upsert by ref, never deletes)
 */
export const importTopology = async (topologyImportRequest: TopologyImportRequest, options?: Parameters<typeof apiFetch>[1]): Promise<TopologyImportResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<TopologyImportResult>(getImportTopologyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(topologyImportRequest)
  }
);}





export const getImportTopologyMutationKey = () => ['importTopology'] as const;

export const getImportTopologyMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importTopology>>, TError,ImportTopologyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importTopology>>, TError,ImportTopologyMutationVariables, TContext> => {

const mutationKey = getImportTopologyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importTopology>>, ImportTopologyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  importTopology(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportTopologyMutationResult = NonNullable<Awaited<ReturnType<typeof importTopology>>>
    export type ImportTopologyMutationBody = TopologyImportRequest
    export type ImportTopologyMutationError = UnprocessableResponse
    export type ImportTopologyMutationVariables = {data: TopologyImportRequest}

    /**
 * @summary Import nodes and links from YAML or CSV (upsert by ref, never deletes)
 */
export const useImportTopology = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importTopology>>, TError,ImportTopologyMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importTopology>>,
        TError,
        ImportTopologyMutationVariables,
        TContext
      > => {
      return useMutation(getImportTopologyMutationOptions(options), queryClient);
    }

export const getListMonitorsUrl = (params?: ListMonitorsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/monitors?${stringifiedParams}` : `/api/v1/monitors`
}

export const listMonitors = async (params?: ListMonitorsParams, options?: Parameters<typeof apiFetch>[1]): Promise<Monitor[]> => {

  return apiFetch<Monitor[]>(getListMonitorsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMonitorsQueryKey = (params?: ListMonitorsParams,) => {
    return [
    `/api/v1/monitors`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMonitorsQueryOptions = <TData = Awaited<ReturnType<typeof listMonitors>>, TError = unknown>(params?: ListMonitorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMonitorsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMonitors>>> = ({ signal }) => listMonitors(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMonitorsQueryResult = NonNullable<Awaited<ReturnType<typeof listMonitors>>>
export type ListMonitorsQueryError = unknown


export function useListMonitors<TData = Awaited<ReturnType<typeof listMonitors>>, TError = unknown>(
 params: undefined |  ListMonitorsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitors>>,
          TError,
          Awaited<ReturnType<typeof listMonitors>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitors<TData = Awaited<ReturnType<typeof listMonitors>>, TError = unknown>(
 params?: ListMonitorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitors>>,
          TError,
          Awaited<ReturnType<typeof listMonitors>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitors<TData = Awaited<ReturnType<typeof listMonitors>>, TError = unknown>(
 params?: ListMonitorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListMonitors<TData = Awaited<ReturnType<typeof listMonitors>>, TError = unknown>(
 params?: ListMonitorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitors>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMonitorsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMonitorUrl = () => {




  return `/api/v1/monitors`
}

export const createMonitor = async (monitorInput: MonitorInput, options?: Parameters<typeof apiFetch>[1]): Promise<Monitor> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Monitor>(getCreateMonitorUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(monitorInput)
  }
);}





export const getCreateMonitorMutationKey = () => ['createMonitor'] as const;

export const getCreateMonitorMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMonitor>>, TError,CreateMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMonitor>>, TError,CreateMonitorMutationVariables, TContext> => {

const mutationKey = getCreateMonitorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMonitor>>, CreateMonitorMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMonitor(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMonitorMutationResult = NonNullable<Awaited<ReturnType<typeof createMonitor>>>
    export type CreateMonitorMutationBody = MonitorInput
    export type CreateMonitorMutationError = UnprocessableResponse
    export type CreateMonitorMutationVariables = {data: MonitorInput}

    export const useCreateMonitor = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMonitor>>, TError,CreateMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createMonitor>>,
        TError,
        CreateMonitorMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMonitorMutationOptions(options), queryClient);
    }

export const getGetMonitorUrl = (id: string,) => {




  return `/api/v1/monitors/${id}`
}

export const getMonitor = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Monitor> => {

  return apiFetch<Monitor>(getGetMonitorUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMonitorQueryKey = (id: string,) => {
    return [
    `/api/v1/monitors/${id}`
    ] as const;
    }


export const getGetMonitorQueryOptions = <TData = Awaited<ReturnType<typeof getMonitor>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMonitorQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMonitor>>> = ({ signal }) => getMonitor(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMonitorQueryResult = NonNullable<Awaited<ReturnType<typeof getMonitor>>>
export type GetMonitorQueryError = NotFoundResponse


export function useGetMonitor<TData = Awaited<ReturnType<typeof getMonitor>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMonitor>>,
          TError,
          Awaited<ReturnType<typeof getMonitor>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMonitor<TData = Awaited<ReturnType<typeof getMonitor>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMonitor>>,
          TError,
          Awaited<ReturnType<typeof getMonitor>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMonitor<TData = Awaited<ReturnType<typeof getMonitor>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetMonitor<TData = Awaited<ReturnType<typeof getMonitor>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitor>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMonitorQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateMonitorUrl = (id: string,) => {




  return `/api/v1/monitors/${id}`
}

export const updateMonitor = async (id: string,
    monitorInput: MonitorInput, options?: Parameters<typeof apiFetch>[1]): Promise<Monitor> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Monitor>(getUpdateMonitorUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(monitorInput)
  }
);}





export const getUpdateMonitorMutationKey = () => ['updateMonitor'] as const;

export const getUpdateMonitorMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMonitor>>, TError,UpdateMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateMonitor>>, TError,UpdateMonitorMutationVariables, TContext> => {

const mutationKey = getUpdateMonitorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateMonitor>>, UpdateMonitorMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateMonitor(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateMonitorMutationResult = NonNullable<Awaited<ReturnType<typeof updateMonitor>>>
    export type UpdateMonitorMutationBody = MonitorInput
    export type UpdateMonitorMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateMonitorMutationVariables = {id: string;data: MonitorInput}

    export const useUpdateMonitor = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMonitor>>, TError,UpdateMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateMonitor>>,
        TError,
        UpdateMonitorMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateMonitorMutationOptions(options), queryClient);
    }

export const getDeleteMonitorUrl = (id: string,) => {




  return `/api/v1/monitors/${id}`
}

export const deleteMonitor = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteMonitorUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteMonitorMutationKey = () => ['deleteMonitor'] as const;

export const getDeleteMonitorMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMonitor>>, TError,DeleteMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteMonitor>>, TError,DeleteMonitorMutationVariables, TContext> => {

const mutationKey = getDeleteMonitorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteMonitor>>, DeleteMonitorMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteMonitor(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteMonitorMutationResult = NonNullable<Awaited<ReturnType<typeof deleteMonitor>>>

    export type DeleteMonitorMutationError = NotFoundResponse
    export type DeleteMonitorMutationVariables = {id: string}

    export const useDeleteMonitor = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMonitor>>, TError,DeleteMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteMonitor>>,
        TError,
        DeleteMonitorMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteMonitorMutationOptions(options), queryClient);
    }

export const getGetMonitorTelemetryUrl = (id: string,
    params: GetMonitorTelemetryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/monitors/${id}/telemetry?${stringifiedParams}` : `/api/v1/monitors/${id}/telemetry`
}

/**
 * @summary Time series for one monitor, per probe
 */
export const getMonitorTelemetry = async (id: string,
    params: GetMonitorTelemetryParams, options?: Parameters<typeof apiFetch>[1]): Promise<TelemetrySeries> => {

  return apiFetch<TelemetrySeries>(getGetMonitorTelemetryUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMonitorTelemetryQueryKey = (id: string,
    params?: GetMonitorTelemetryParams,) => {
    return [
    `/api/v1/monitors/${id}/telemetry`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMonitorTelemetryQueryOptions = <TData = Awaited<ReturnType<typeof getMonitorTelemetry>>, TError = NotFoundResponse | UnprocessableResponse>(id: string,
    params: GetMonitorTelemetryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMonitorTelemetryQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMonitorTelemetry>>> = ({ signal }) => getMonitorTelemetry(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMonitorTelemetryQueryResult = NonNullable<Awaited<ReturnType<typeof getMonitorTelemetry>>>
export type GetMonitorTelemetryQueryError = NotFoundResponse | UnprocessableResponse


export function useGetMonitorTelemetry<TData = Awaited<ReturnType<typeof getMonitorTelemetry>>, TError = NotFoundResponse | UnprocessableResponse>(
 id: string,
    params: GetMonitorTelemetryParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMonitorTelemetry>>,
          TError,
          Awaited<ReturnType<typeof getMonitorTelemetry>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMonitorTelemetry<TData = Awaited<ReturnType<typeof getMonitorTelemetry>>, TError = NotFoundResponse | UnprocessableResponse>(
 id: string,
    params: GetMonitorTelemetryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMonitorTelemetry>>,
          TError,
          Awaited<ReturnType<typeof getMonitorTelemetry>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMonitorTelemetry<TData = Awaited<ReturnType<typeof getMonitorTelemetry>>, TError = NotFoundResponse | UnprocessableResponse>(
 id: string,
    params: GetMonitorTelemetryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Time series for one monitor, per probe
 */

export function useGetMonitorTelemetry<TData = Awaited<ReturnType<typeof getMonitorTelemetry>>, TError = NotFoundResponse | UnprocessableResponse>(
 id: string,
    params: GetMonitorTelemetryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMonitorTelemetry>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMonitorTelemetryQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListEnrollmentTokensUrl = () => {




  return `/api/v1/enrollment-tokens`
}

export const listEnrollmentTokens = async ( options?: Parameters<typeof apiFetch>[1]): Promise<EnrollmentToken[]> => {

  return apiFetch<EnrollmentToken[]>(getListEnrollmentTokensUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListEnrollmentTokensQueryKey = () => {
    return [
    `/api/v1/enrollment-tokens`
    ] as const;
    }


export const getListEnrollmentTokensQueryOptions = <TData = Awaited<ReturnType<typeof listEnrollmentTokens>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEnrollmentTokensQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEnrollmentTokens>>> = ({ signal }) => listEnrollmentTokens({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListEnrollmentTokensQueryResult = NonNullable<Awaited<ReturnType<typeof listEnrollmentTokens>>>
export type ListEnrollmentTokensQueryError = unknown


export function useListEnrollmentTokens<TData = Awaited<ReturnType<typeof listEnrollmentTokens>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listEnrollmentTokens>>,
          TError,
          Awaited<ReturnType<typeof listEnrollmentTokens>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListEnrollmentTokens<TData = Awaited<ReturnType<typeof listEnrollmentTokens>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listEnrollmentTokens>>,
          TError,
          Awaited<ReturnType<typeof listEnrollmentTokens>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListEnrollmentTokens<TData = Awaited<ReturnType<typeof listEnrollmentTokens>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListEnrollmentTokens<TData = Awaited<ReturnType<typeof listEnrollmentTokens>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEnrollmentTokens>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListEnrollmentTokensQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateEnrollmentTokenUrl = () => {




  return `/api/v1/enrollment-tokens`
}

export const createEnrollmentToken = async (enrollmentTokenInput: EnrollmentTokenInput, options?: Parameters<typeof apiFetch>[1]): Promise<EnrollmentTokenCreated> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<EnrollmentTokenCreated>(getCreateEnrollmentTokenUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(enrollmentTokenInput)
  }
);}





export const getCreateEnrollmentTokenMutationKey = () => ['createEnrollmentToken'] as const;

export const getCreateEnrollmentTokenMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEnrollmentToken>>, TError,CreateEnrollmentTokenMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createEnrollmentToken>>, TError,CreateEnrollmentTokenMutationVariables, TContext> => {

const mutationKey = getCreateEnrollmentTokenMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEnrollmentToken>>, CreateEnrollmentTokenMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createEnrollmentToken(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEnrollmentTokenMutationResult = NonNullable<Awaited<ReturnType<typeof createEnrollmentToken>>>
    export type CreateEnrollmentTokenMutationBody = EnrollmentTokenInput
    export type CreateEnrollmentTokenMutationError = UnprocessableResponse
    export type CreateEnrollmentTokenMutationVariables = {data: EnrollmentTokenInput}

    export const useCreateEnrollmentToken = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEnrollmentToken>>, TError,CreateEnrollmentTokenMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createEnrollmentToken>>,
        TError,
        CreateEnrollmentTokenMutationVariables,
        TContext
      > => {
      return useMutation(getCreateEnrollmentTokenMutationOptions(options), queryClient);
    }

export const getRevokeEnrollmentTokenUrl = (id: string,) => {




  return `/api/v1/enrollment-tokens/${id}/revoke`
}

export const revokeEnrollmentToken = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<EnrollmentToken> => {

  return apiFetch<EnrollmentToken>(getRevokeEnrollmentTokenUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRevokeEnrollmentTokenMutationKey = () => ['revokeEnrollmentToken'] as const;

export const getRevokeEnrollmentTokenMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeEnrollmentToken>>, TError,RevokeEnrollmentTokenMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeEnrollmentToken>>, TError,RevokeEnrollmentTokenMutationVariables, TContext> => {

const mutationKey = getRevokeEnrollmentTokenMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeEnrollmentToken>>, RevokeEnrollmentTokenMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  revokeEnrollmentToken(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeEnrollmentTokenMutationResult = NonNullable<Awaited<ReturnType<typeof revokeEnrollmentToken>>>

    export type RevokeEnrollmentTokenMutationError = NotFoundResponse
    export type RevokeEnrollmentTokenMutationVariables = {id: string}

    export const useRevokeEnrollmentToken = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeEnrollmentToken>>, TError,RevokeEnrollmentTokenMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof revokeEnrollmentToken>>,
        TError,
        RevokeEnrollmentTokenMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeEnrollmentTokenMutationOptions(options), queryClient);
    }

export const getListProbesUrl = (params?: ListProbesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/probes?${stringifiedParams}` : `/api/v1/probes`
}

export const listProbes = async (params?: ListProbesParams, options?: Parameters<typeof apiFetch>[1]): Promise<Probe[]> => {

  return apiFetch<Probe[]>(getListProbesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProbesQueryKey = (params?: ListProbesParams,) => {
    return [
    `/api/v1/probes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListProbesQueryOptions = <TData = Awaited<ReturnType<typeof listProbes>>, TError = unknown>(params?: ListProbesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProbesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProbes>>> = ({ signal }) => listProbes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListProbesQueryResult = NonNullable<Awaited<ReturnType<typeof listProbes>>>
export type ListProbesQueryError = unknown


export function useListProbes<TData = Awaited<ReturnType<typeof listProbes>>, TError = unknown>(
 params: undefined |  ListProbesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbes>>,
          TError,
          Awaited<ReturnType<typeof listProbes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbes<TData = Awaited<ReturnType<typeof listProbes>>, TError = unknown>(
 params?: ListProbesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbes>>,
          TError,
          Awaited<ReturnType<typeof listProbes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbes<TData = Awaited<ReturnType<typeof listProbes>>, TError = unknown>(
 params?: ListProbesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListProbes<TData = Awaited<ReturnType<typeof listProbes>>, TError = unknown>(
 params?: ListProbesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListProbesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetProbeUrl = (id: string,) => {




  return `/api/v1/probes/${id}`
}

export const getProbe = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Probe> => {

  return apiFetch<Probe>(getGetProbeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetProbeQueryKey = (id: string,) => {
    return [
    `/api/v1/probes/${id}`
    ] as const;
    }


export const getGetProbeQueryOptions = <TData = Awaited<ReturnType<typeof getProbe>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProbeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProbe>>> = ({ signal }) => getProbe(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetProbeQueryResult = NonNullable<Awaited<ReturnType<typeof getProbe>>>
export type GetProbeQueryError = NotFoundResponse


export function useGetProbe<TData = Awaited<ReturnType<typeof getProbe>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProbe>>,
          TError,
          Awaited<ReturnType<typeof getProbe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProbe<TData = Awaited<ReturnType<typeof getProbe>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProbe>>,
          TError,
          Awaited<ReturnType<typeof getProbe>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProbe<TData = Awaited<ReturnType<typeof getProbe>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetProbe<TData = Awaited<ReturnType<typeof getProbe>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbe>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetProbeQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateProbeUrl = (id: string,) => {




  return `/api/v1/probes/${id}`
}

export const updateProbe = async (id: string,
    probeInput: ProbeInput, options?: Parameters<typeof apiFetch>[1]): Promise<Probe> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Probe>(getUpdateProbeUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(probeInput)
  }
);}





export const getUpdateProbeMutationKey = () => ['updateProbe'] as const;

export const getUpdateProbeMutationOptions = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProbe>>, TError,UpdateProbeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateProbe>>, TError,UpdateProbeMutationVariables, TContext> => {

const mutationKey = getUpdateProbeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateProbe>>, UpdateProbeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateProbe(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateProbeMutationResult = NonNullable<Awaited<ReturnType<typeof updateProbe>>>
    export type UpdateProbeMutationBody = ProbeInput
    export type UpdateProbeMutationError = NotFoundResponse | ConflictResponse | UnprocessableResponse
    export type UpdateProbeMutationVariables = {id: string;data: ProbeInput}

    export const useUpdateProbe = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateProbe>>, TError,UpdateProbeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateProbe>>,
        TError,
        UpdateProbeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateProbeMutationOptions(options), queryClient);
    }

export const getRevokeProbeUrl = (id: string,) => {




  return `/api/v1/probes/${id}/revoke`
}

/**
 * @summary Close the probe's session now and reject future connections
 */
export const revokeProbe = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Probe> => {

  return apiFetch<Probe>(getRevokeProbeUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRevokeProbeMutationKey = () => ['revokeProbe'] as const;

export const getRevokeProbeMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeProbe>>, TError,RevokeProbeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof revokeProbe>>, TError,RevokeProbeMutationVariables, TContext> => {

const mutationKey = getRevokeProbeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof revokeProbe>>, RevokeProbeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  revokeProbe(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RevokeProbeMutationResult = NonNullable<Awaited<ReturnType<typeof revokeProbe>>>

    export type RevokeProbeMutationError = NotFoundResponse
    export type RevokeProbeMutationVariables = {id: string}

    /**
 * @summary Close the probe's session now and reject future connections
 */
export const useRevokeProbe = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof revokeProbe>>, TError,RevokeProbeMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof revokeProbe>>,
        TError,
        RevokeProbeMutationVariables,
        TContext
      > => {
      return useMutation(getRevokeProbeMutationOptions(options), queryClient);
    }

export const getListProbeReleasesUrl = () => {




  return `/api/v1/probe-releases`
}

export const listProbeReleases = async ( options?: Parameters<typeof apiFetch>[1]): Promise<ProbeReleaseList> => {

  return apiFetch<ProbeReleaseList>(getListProbeReleasesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProbeReleasesQueryKey = () => {
    return [
    `/api/v1/probe-releases`
    ] as const;
    }


export const getListProbeReleasesQueryOptions = <TData = Awaited<ReturnType<typeof listProbeReleases>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProbeReleasesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProbeReleases>>> = ({ signal }) => listProbeReleases({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListProbeReleasesQueryResult = NonNullable<Awaited<ReturnType<typeof listProbeReleases>>>
export type ListProbeReleasesQueryError = unknown


export function useListProbeReleases<TData = Awaited<ReturnType<typeof listProbeReleases>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbeReleases>>,
          TError,
          Awaited<ReturnType<typeof listProbeReleases>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbeReleases<TData = Awaited<ReturnType<typeof listProbeReleases>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbeReleases>>,
          TError,
          Awaited<ReturnType<typeof listProbeReleases>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbeReleases<TData = Awaited<ReturnType<typeof listProbeReleases>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListProbeReleases<TData = Awaited<ReturnType<typeof listProbeReleases>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeReleases>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListProbeReleasesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListProbeRolloutsUrl = () => {




  return `/api/v1/probe-rollouts`
}

/**
 * @summary Recent fleet upgrades, newest first (without their targets)
 */
export const listProbeRollouts = async ( options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout[]> => {

  return apiFetch<ProbeRollout[]>(getListProbeRolloutsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListProbeRolloutsQueryKey = () => {
    return [
    `/api/v1/probe-rollouts`
    ] as const;
    }


export const getListProbeRolloutsQueryOptions = <TData = Awaited<ReturnType<typeof listProbeRollouts>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListProbeRolloutsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listProbeRollouts>>> = ({ signal }) => listProbeRollouts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListProbeRolloutsQueryResult = NonNullable<Awaited<ReturnType<typeof listProbeRollouts>>>
export type ListProbeRolloutsQueryError = unknown


export function useListProbeRollouts<TData = Awaited<ReturnType<typeof listProbeRollouts>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbeRollouts>>,
          TError,
          Awaited<ReturnType<typeof listProbeRollouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbeRollouts<TData = Awaited<ReturnType<typeof listProbeRollouts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProbeRollouts>>,
          TError,
          Awaited<ReturnType<typeof listProbeRollouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListProbeRollouts<TData = Awaited<ReturnType<typeof listProbeRollouts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Recent fleet upgrades, newest first (without their targets)
 */

export function useListProbeRollouts<TData = Awaited<ReturnType<typeof listProbeRollouts>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listProbeRollouts>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListProbeRolloutsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateProbeRolloutUrl = () => {




  return `/api/v1/probe-rollouts`
}

/**
 * docs/adr/2026-09-28-probe-fleet-upgrades.md. Without probe_ids, every active probe on an older version is upgraded. Only one rollout runs at a time.
 * @summary Upgrade probes to a published release, at most 10 % at a time
 */
export const createProbeRollout = async (probeRolloutInput: ProbeRolloutInput, options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<ProbeRollout>(getCreateProbeRolloutUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(probeRolloutInput)
  }
);}





export const getCreateProbeRolloutMutationKey = () => ['createProbeRollout'] as const;

export const getCreateProbeRolloutMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProbeRollout>>, TError,CreateProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createProbeRollout>>, TError,CreateProbeRolloutMutationVariables, TContext> => {

const mutationKey = getCreateProbeRolloutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createProbeRollout>>, CreateProbeRolloutMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createProbeRollout(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateProbeRolloutMutationResult = NonNullable<Awaited<ReturnType<typeof createProbeRollout>>>
    export type CreateProbeRolloutMutationBody = ProbeRolloutInput
    export type CreateProbeRolloutMutationError = ConflictResponse | UnprocessableResponse
    export type CreateProbeRolloutMutationVariables = {data: ProbeRolloutInput}

    /**
 * @summary Upgrade probes to a published release, at most 10 % at a time
 */
export const useCreateProbeRollout = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createProbeRollout>>, TError,CreateProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createProbeRollout>>,
        TError,
        CreateProbeRolloutMutationVariables,
        TContext
      > => {
      return useMutation(getCreateProbeRolloutMutationOptions(options), queryClient);
    }

export const getGetProbeRolloutUrl = (id: string,) => {




  return `/api/v1/probe-rollouts/${id}`
}

export const getProbeRollout = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout> => {

  return apiFetch<ProbeRollout>(getGetProbeRolloutUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetProbeRolloutQueryKey = (id: string,) => {
    return [
    `/api/v1/probe-rollouts/${id}`
    ] as const;
    }


export const getGetProbeRolloutQueryOptions = <TData = Awaited<ReturnType<typeof getProbeRollout>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetProbeRolloutQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getProbeRollout>>> = ({ signal }) => getProbeRollout(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetProbeRolloutQueryResult = NonNullable<Awaited<ReturnType<typeof getProbeRollout>>>
export type GetProbeRolloutQueryError = NotFoundResponse


export function useGetProbeRollout<TData = Awaited<ReturnType<typeof getProbeRollout>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProbeRollout>>,
          TError,
          Awaited<ReturnType<typeof getProbeRollout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProbeRollout<TData = Awaited<ReturnType<typeof getProbeRollout>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProbeRollout>>,
          TError,
          Awaited<ReturnType<typeof getProbeRollout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetProbeRollout<TData = Awaited<ReturnType<typeof getProbeRollout>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetProbeRollout<TData = Awaited<ReturnType<typeof getProbeRollout>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getProbeRollout>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetProbeRolloutQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPauseProbeRolloutUrl = (id: string,) => {




  return `/api/v1/probe-rollouts/${id}/pause`
}

/**
 * @summary Send no more upgrade requests; probes already upgrading finish
 */
export const pauseProbeRollout = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout> => {

  return apiFetch<ProbeRollout>(getPauseProbeRolloutUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPauseProbeRolloutMutationKey = () => ['pauseProbeRollout'] as const;

export const getPauseProbeRolloutMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseProbeRollout>>, TError,PauseProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof pauseProbeRollout>>, TError,PauseProbeRolloutMutationVariables, TContext> => {

const mutationKey = getPauseProbeRolloutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof pauseProbeRollout>>, PauseProbeRolloutMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  pauseProbeRollout(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PauseProbeRolloutMutationResult = NonNullable<Awaited<ReturnType<typeof pauseProbeRollout>>>

    export type PauseProbeRolloutMutationError = NotFoundResponse | ConflictResponse
    export type PauseProbeRolloutMutationVariables = {id: string}

    /**
 * @summary Send no more upgrade requests; probes already upgrading finish
 */
export const usePauseProbeRollout = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof pauseProbeRollout>>, TError,PauseProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof pauseProbeRollout>>,
        TError,
        PauseProbeRolloutMutationVariables,
        TContext
      > => {
      return useMutation(getPauseProbeRolloutMutationOptions(options), queryClient);
    }

export const getResumeProbeRolloutUrl = (id: string,) => {




  return `/api/v1/probe-rollouts/${id}/resume`
}

/**
 * @summary Continue a paused or halted rollout; failures so far no longer halt it
 */
export const resumeProbeRollout = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout> => {

  return apiFetch<ProbeRollout>(getResumeProbeRolloutUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getResumeProbeRolloutMutationKey = () => ['resumeProbeRollout'] as const;

export const getResumeProbeRolloutMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeProbeRollout>>, TError,ResumeProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resumeProbeRollout>>, TError,ResumeProbeRolloutMutationVariables, TContext> => {

const mutationKey = getResumeProbeRolloutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resumeProbeRollout>>, ResumeProbeRolloutMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  resumeProbeRollout(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResumeProbeRolloutMutationResult = NonNullable<Awaited<ReturnType<typeof resumeProbeRollout>>>

    export type ResumeProbeRolloutMutationError = NotFoundResponse | ConflictResponse
    export type ResumeProbeRolloutMutationVariables = {id: string}

    /**
 * @summary Continue a paused or halted rollout; failures so far no longer halt it
 */
export const useResumeProbeRollout = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resumeProbeRollout>>, TError,ResumeProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof resumeProbeRollout>>,
        TError,
        ResumeProbeRolloutMutationVariables,
        TContext
      > => {
      return useMutation(getResumeProbeRolloutMutationOptions(options), queryClient);
    }

export const getCancelProbeRolloutUrl = (id: string,) => {




  return `/api/v1/probe-rollouts/${id}/cancel`
}

/**
 * @summary Stop the rollout; probes not yet upgraded are skipped
 */
export const cancelProbeRollout = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<ProbeRollout> => {

  return apiFetch<ProbeRollout>(getCancelProbeRolloutUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelProbeRolloutMutationKey = () => ['cancelProbeRollout'] as const;

export const getCancelProbeRolloutMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelProbeRollout>>, TError,CancelProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelProbeRollout>>, TError,CancelProbeRolloutMutationVariables, TContext> => {

const mutationKey = getCancelProbeRolloutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelProbeRollout>>, CancelProbeRolloutMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelProbeRollout(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelProbeRolloutMutationResult = NonNullable<Awaited<ReturnType<typeof cancelProbeRollout>>>

    export type CancelProbeRolloutMutationError = NotFoundResponse | ConflictResponse
    export type CancelProbeRolloutMutationVariables = {id: string}

    /**
 * @summary Stop the rollout; probes not yet upgraded are skipped
 */
export const useCancelProbeRollout = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelProbeRollout>>, TError,CancelProbeRolloutMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelProbeRollout>>,
        TError,
        CancelProbeRolloutMutationVariables,
        TContext
      > => {
      return useMutation(getCancelProbeRolloutMutationOptions(options), queryClient);
    }

export const getListMonitorStatesUrl = () => {




  return `/api/v1/monitor-states`
}

/**
 * @summary Live effective and per-probe state of every visible monitor
 */
export const listMonitorStates = async ( options?: Parameters<typeof apiFetch>[1]): Promise<MonitorState[]> => {

  return apiFetch<MonitorState[]>(getListMonitorStatesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMonitorStatesQueryKey = () => {
    return [
    `/api/v1/monitor-states`
    ] as const;
    }


export const getListMonitorStatesQueryOptions = <TData = Awaited<ReturnType<typeof listMonitorStates>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMonitorStatesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMonitorStates>>> = ({ signal }) => listMonitorStates({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMonitorStatesQueryResult = NonNullable<Awaited<ReturnType<typeof listMonitorStates>>>
export type ListMonitorStatesQueryError = unknown


export function useListMonitorStates<TData = Awaited<ReturnType<typeof listMonitorStates>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorStates>>,
          TError,
          Awaited<ReturnType<typeof listMonitorStates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorStates<TData = Awaited<ReturnType<typeof listMonitorStates>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorStates>>,
          TError,
          Awaited<ReturnType<typeof listMonitorStates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorStates<TData = Awaited<ReturnType<typeof listMonitorStates>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Live effective and per-probe state of every visible monitor
 */

export function useListMonitorStates<TData = Awaited<ReturnType<typeof listMonitorStates>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMonitorStatesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMonitorStatusChangesUrl = (id: string,
    params?: ListMonitorStatusChangesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/monitors/${id}/status-changes?${stringifiedParams}` : `/api/v1/monitors/${id}/status-changes`
}

/**
 * @summary State timeline (newest first, max 500)
 */
export const listMonitorStatusChanges = async (id: string,
    params?: ListMonitorStatusChangesParams, options?: Parameters<typeof apiFetch>[1]): Promise<StatusChange[]> => {

  return apiFetch<StatusChange[]>(getListMonitorStatusChangesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMonitorStatusChangesQueryKey = (id: string,
    params?: ListMonitorStatusChangesParams,) => {
    return [
    `/api/v1/monitors/${id}/status-changes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMonitorStatusChangesQueryOptions = <TData = Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError = NotFoundResponse>(id: string,
    params?: ListMonitorStatusChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMonitorStatusChangesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMonitorStatusChanges>>> = ({ signal }) => listMonitorStatusChanges(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMonitorStatusChangesQueryResult = NonNullable<Awaited<ReturnType<typeof listMonitorStatusChanges>>>
export type ListMonitorStatusChangesQueryError = NotFoundResponse


export function useListMonitorStatusChanges<TData = Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError = NotFoundResponse>(
 id: string,
    params: undefined |  ListMonitorStatusChangesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorStatusChanges>>,
          TError,
          Awaited<ReturnType<typeof listMonitorStatusChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorStatusChanges<TData = Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorStatusChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorStatusChanges>>,
          TError,
          Awaited<ReturnType<typeof listMonitorStatusChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorStatusChanges<TData = Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorStatusChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary State timeline (newest first, max 500)
 */

export function useListMonitorStatusChanges<TData = Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorStatusChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorStatusChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMonitorStatusChangesQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMonitorPathsUrl = (id: string,) => {




  return `/api/v1/monitors/${id}/paths`
}

/**
 * docs/adr/2026-10-04-as-path-monitoring.md §4–§5. 404 for monitors that are not PATH monitors.
 * @summary Each assigned probe's AS path towards a PATH monitor's target
 */
export const listMonitorPaths = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<MonitorPath[]> => {

  return apiFetch<MonitorPath[]>(getListMonitorPathsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMonitorPathsQueryKey = (id: string,) => {
    return [
    `/api/v1/monitors/${id}/paths`
    ] as const;
    }


export const getListMonitorPathsQueryOptions = <TData = Awaited<ReturnType<typeof listMonitorPaths>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMonitorPathsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMonitorPaths>>> = ({ signal }) => listMonitorPaths(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMonitorPathsQueryResult = NonNullable<Awaited<ReturnType<typeof listMonitorPaths>>>
export type ListMonitorPathsQueryError = NotFoundResponse


export function useListMonitorPaths<TData = Awaited<ReturnType<typeof listMonitorPaths>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorPaths>>,
          TError,
          Awaited<ReturnType<typeof listMonitorPaths>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorPaths<TData = Awaited<ReturnType<typeof listMonitorPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorPaths>>,
          TError,
          Awaited<ReturnType<typeof listMonitorPaths>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorPaths<TData = Awaited<ReturnType<typeof listMonitorPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Each assigned probe's AS path towards a PATH monitor's target
 */

export function useListMonitorPaths<TData = Awaited<ReturnType<typeof listMonitorPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMonitorPathsQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getWatchMonitorBgpPrefixesUrl = (id: string,) => {




  return `/api/v1/monitors/${id}/bgp-prefixes`
}

/**
 * docs/adr/2026-10-04-external-prefix-watches.md §1. Every distinct address the probes' latest traces reached (or the target, when it is an IP address and nothing was traced yet) is looked up through RIPEstat network-info; prefixes already watched are only linked.
 * @summary Watch the BGP prefixes covering a PATH monitor's target
 */
export const watchMonitorBgpPrefixes = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<BgpWatchResult> => {

  return apiFetch<BgpWatchResult>(getWatchMonitorBgpPrefixesUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getWatchMonitorBgpPrefixesMutationKey = () => ['watchMonitorBgpPrefixes'] as const;

export const getWatchMonitorBgpPrefixesMutationOptions = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>, TError,WatchMonitorBgpPrefixesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>, TError,WatchMonitorBgpPrefixesMutationVariables, TContext> => {

const mutationKey = getWatchMonitorBgpPrefixesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>, WatchMonitorBgpPrefixesMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  watchMonitorBgpPrefixes(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type WatchMonitorBgpPrefixesMutationResult = NonNullable<Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>>

    export type WatchMonitorBgpPrefixesMutationError = NotFoundResponse | ConflictResponse | UnprocessableResponse
    export type WatchMonitorBgpPrefixesMutationVariables = {id: string}

    /**
 * @summary Watch the BGP prefixes covering a PATH monitor's target
 */
export const useWatchMonitorBgpPrefixes = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>, TError,WatchMonitorBgpPrefixesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof watchMonitorBgpPrefixes>>,
        TError,
        WatchMonitorBgpPrefixesMutationVariables,
        TContext
      > => {
      return useMutation(getWatchMonitorBgpPrefixesMutationOptions(options), queryClient);
    }

export const getUnlinkMonitorBgpPrefixUrl = (id: string,
    prefixId: string,) => {




  return `/api/v1/monitors/${id}/bgp-prefixes/${prefixId}`
}

/**
 * @summary Unlink a watched prefix from a monitor (the watch stays)
 */
export const unlinkMonitorBgpPrefix = async (id: string,
    prefixId: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getUnlinkMonitorBgpPrefixUrl(id,prefixId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getUnlinkMonitorBgpPrefixMutationKey = () => ['unlinkMonitorBgpPrefix'] as const;

export const getUnlinkMonitorBgpPrefixMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>, TError,UnlinkMonitorBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>, TError,UnlinkMonitorBgpPrefixMutationVariables, TContext> => {

const mutationKey = getUnlinkMonitorBgpPrefixMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>, UnlinkMonitorBgpPrefixMutationVariables> = (props) => {
          const {id,prefixId} = props ?? {};

          return  unlinkMonitorBgpPrefix(id,prefixId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UnlinkMonitorBgpPrefixMutationResult = NonNullable<Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>>

    export type UnlinkMonitorBgpPrefixMutationError = NotFoundResponse
    export type UnlinkMonitorBgpPrefixMutationVariables = {id: string;prefixId: string}

    /**
 * @summary Unlink a watched prefix from a monitor (the watch stays)
 */
export const useUnlinkMonitorBgpPrefix = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>, TError,UnlinkMonitorBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof unlinkMonitorBgpPrefix>>,
        TError,
        UnlinkMonitorBgpPrefixMutationVariables,
        TContext
      > => {
      return useMutation(getUnlinkMonitorBgpPrefixMutationOptions(options), queryClient);
    }

export const getAcceptMonitorPathUrl = (id: string,
    probeId: string,) => {




  return `/api/v1/monitors/${id}/paths/${probeId}/accept`
}

/**
 * Resolves the monitor's PATH_CHANGE incident when no other probe's path deviates. Audit-logged and recorded on the incident. 409 when the probe has no path yet (no_path) or the path breaks the monitor's ASN rules (path_violates_rules).
 * @summary Make a probe's current path its baseline
 */
export const acceptMonitorPath = async (id: string,
    probeId: string, options?: Parameters<typeof apiFetch>[1]): Promise<MonitorPath> => {

  return apiFetch<MonitorPath>(getAcceptMonitorPathUrl(id,probeId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAcceptMonitorPathMutationKey = () => ['acceptMonitorPath'] as const;

export const getAcceptMonitorPathMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptMonitorPath>>, TError,AcceptMonitorPathMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptMonitorPath>>, TError,AcceptMonitorPathMutationVariables, TContext> => {

const mutationKey = getAcceptMonitorPathMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptMonitorPath>>, AcceptMonitorPathMutationVariables> = (props) => {
          const {id,probeId} = props ?? {};

          return  acceptMonitorPath(id,probeId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptMonitorPathMutationResult = NonNullable<Awaited<ReturnType<typeof acceptMonitorPath>>>

    export type AcceptMonitorPathMutationError = NotFoundResponse | ConflictResponse
    export type AcceptMonitorPathMutationVariables = {id: string;probeId: string}

    /**
 * @summary Make a probe's current path its baseline
 */
export const useAcceptMonitorPath = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptMonitorPath>>, TError,AcceptMonitorPathMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof acceptMonitorPath>>,
        TError,
        AcceptMonitorPathMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptMonitorPathMutationOptions(options), queryClient);
    }

export const getListMonitorPathChangesUrl = (id: string,
    params?: ListMonitorPathChangesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/monitors/${id}/path-changes?${stringifiedParams}` : `/api/v1/monitors/${id}/path-changes`
}

/**
 * @summary Path history of a PATH monitor (newest first, max 500)
 */
export const listMonitorPathChanges = async (id: string,
    params?: ListMonitorPathChangesParams, options?: Parameters<typeof apiFetch>[1]): Promise<PathChange[]> => {

  return apiFetch<PathChange[]>(getListMonitorPathChangesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMonitorPathChangesQueryKey = (id: string,
    params?: ListMonitorPathChangesParams,) => {
    return [
    `/api/v1/monitors/${id}/path-changes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMonitorPathChangesQueryOptions = <TData = Awaited<ReturnType<typeof listMonitorPathChanges>>, TError = NotFoundResponse>(id: string,
    params?: ListMonitorPathChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMonitorPathChangesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMonitorPathChanges>>> = ({ signal }) => listMonitorPathChanges(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMonitorPathChangesQueryResult = NonNullable<Awaited<ReturnType<typeof listMonitorPathChanges>>>
export type ListMonitorPathChangesQueryError = NotFoundResponse


export function useListMonitorPathChanges<TData = Awaited<ReturnType<typeof listMonitorPathChanges>>, TError = NotFoundResponse>(
 id: string,
    params: undefined |  ListMonitorPathChangesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorPathChanges>>,
          TError,
          Awaited<ReturnType<typeof listMonitorPathChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorPathChanges<TData = Awaited<ReturnType<typeof listMonitorPathChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorPathChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMonitorPathChanges>>,
          TError,
          Awaited<ReturnType<typeof listMonitorPathChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMonitorPathChanges<TData = Awaited<ReturnType<typeof listMonitorPathChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorPathChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Path history of a PATH monitor (newest first, max 500)
 */

export function useListMonitorPathChanges<TData = Awaited<ReturnType<typeof listMonitorPathChanges>>, TError = NotFoundResponse>(
 id: string,
    params?: ListMonitorPathChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMonitorPathChanges>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMonitorPathChangesQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMuteMonitorUrl = (id: string,) => {




  return `/api/v1/monitors/${id}/mutes`
}

/**
 * @summary Silence notifications for a monitor (reason and duration required)
 */
export const muteMonitor = async (id: string,
    muteInput: MuteInput, options?: Parameters<typeof apiFetch>[1]): Promise<Mute> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Mute>(getMuteMonitorUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(muteInput)
  }
);}





export const getMuteMonitorMutationKey = () => ['muteMonitor'] as const;

export const getMuteMonitorMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof muteMonitor>>, TError,MuteMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof muteMonitor>>, TError,MuteMonitorMutationVariables, TContext> => {

const mutationKey = getMuteMonitorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof muteMonitor>>, MuteMonitorMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  muteMonitor(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MuteMonitorMutationResult = NonNullable<Awaited<ReturnType<typeof muteMonitor>>>
    export type MuteMonitorMutationBody = MuteInput
    export type MuteMonitorMutationError = NotFoundResponse | UnprocessableResponse
    export type MuteMonitorMutationVariables = {id: string;data: MuteInput}

    /**
 * @summary Silence notifications for a monitor (reason and duration required)
 */
export const useMuteMonitor = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof muteMonitor>>, TError,MuteMonitorMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof muteMonitor>>,
        TError,
        MuteMonitorMutationVariables,
        TContext
      > => {
      return useMutation(getMuteMonitorMutationOptions(options), queryClient);
    }

export const getListMutesUrl = () => {




  return `/api/v1/mutes`
}

export const listMutes = async ( options?: Parameters<typeof apiFetch>[1]): Promise<Mute[]> => {

  return apiFetch<Mute[]>(getListMutesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMutesQueryKey = () => {
    return [
    `/api/v1/mutes`
    ] as const;
    }


export const getListMutesQueryOptions = <TData = Awaited<ReturnType<typeof listMutes>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMutesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMutes>>> = ({ signal }) => listMutes({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMutesQueryResult = NonNullable<Awaited<ReturnType<typeof listMutes>>>
export type ListMutesQueryError = unknown


export function useListMutes<TData = Awaited<ReturnType<typeof listMutes>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMutes>>,
          TError,
          Awaited<ReturnType<typeof listMutes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMutes<TData = Awaited<ReturnType<typeof listMutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMutes>>,
          TError,
          Awaited<ReturnType<typeof listMutes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMutes<TData = Awaited<ReturnType<typeof listMutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListMutes<TData = Awaited<ReturnType<typeof listMutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMutesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelMuteUrl = (id: string,) => {




  return `/api/v1/mutes/${id}`
}

export const cancelMute = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getCancelMuteUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getCancelMuteMutationKey = () => ['cancelMute'] as const;

export const getCancelMuteMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelMute>>, TError,CancelMuteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelMute>>, TError,CancelMuteMutationVariables, TContext> => {

const mutationKey = getCancelMuteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelMute>>, CancelMuteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelMute(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelMuteMutationResult = NonNullable<Awaited<ReturnType<typeof cancelMute>>>

    export type CancelMuteMutationError = NotFoundResponse
    export type CancelMuteMutationVariables = {id: string}

    export const useCancelMute = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelMute>>, TError,CancelMuteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelMute>>,
        TError,
        CancelMuteMutationVariables,
        TContext
      > => {
      return useMutation(getCancelMuteMutationOptions(options), queryClient);
    }

export const getListIncidentsUrl = (params?: ListIncidentsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/incidents?${stringifiedParams}` : `/api/v1/incidents`
}

export const listIncidents = async (params?: ListIncidentsParams, options?: Parameters<typeof apiFetch>[1]): Promise<Incident[]> => {

  return apiFetch<Incident[]>(getListIncidentsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListIncidentsQueryKey = (params?: ListIncidentsParams,) => {
    return [
    `/api/v1/incidents`, ...(params ? [params] : [])
    ] as const;
    }


export const getListIncidentsQueryOptions = <TData = Awaited<ReturnType<typeof listIncidents>>, TError = unknown>(params?: ListIncidentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListIncidentsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listIncidents>>> = ({ signal }) => listIncidents(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListIncidentsQueryResult = NonNullable<Awaited<ReturnType<typeof listIncidents>>>
export type ListIncidentsQueryError = unknown


export function useListIncidents<TData = Awaited<ReturnType<typeof listIncidents>>, TError = unknown>(
 params: undefined |  ListIncidentsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listIncidents>>,
          TError,
          Awaited<ReturnType<typeof listIncidents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListIncidents<TData = Awaited<ReturnType<typeof listIncidents>>, TError = unknown>(
 params?: ListIncidentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listIncidents>>,
          TError,
          Awaited<ReturnType<typeof listIncidents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListIncidents<TData = Awaited<ReturnType<typeof listIncidents>>, TError = unknown>(
 params?: ListIncidentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListIncidents<TData = Awaited<ReturnType<typeof listIncidents>>, TError = unknown>(
 params?: ListIncidentsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listIncidents>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListIncidentsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetIncidentUrl = (id: string,) => {




  return `/api/v1/incidents/${id}`
}

export const getIncident = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<IncidentDetail> => {

  return apiFetch<IncidentDetail>(getGetIncidentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIncidentQueryKey = (id: string,) => {
    return [
    `/api/v1/incidents/${id}`
    ] as const;
    }


export const getGetIncidentQueryOptions = <TData = Awaited<ReturnType<typeof getIncident>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIncidentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIncident>>> = ({ signal }) => getIncident(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetIncidentQueryResult = NonNullable<Awaited<ReturnType<typeof getIncident>>>
export type GetIncidentQueryError = NotFoundResponse


export function useGetIncident<TData = Awaited<ReturnType<typeof getIncident>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncident>>,
          TError,
          Awaited<ReturnType<typeof getIncident>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIncident<TData = Awaited<ReturnType<typeof getIncident>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIncident>>,
          TError,
          Awaited<ReturnType<typeof getIncident>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIncident<TData = Awaited<ReturnType<typeof getIncident>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetIncident<TData = Awaited<ReturnType<typeof getIncident>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIncident>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetIncidentQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAckIncidentUrl = (id: string,) => {




  return `/api/v1/incidents/${id}/ack`
}

/**
 * @summary Acknowledge (stops escalation, records who and when)
 */
export const ackIncident = async (id: string,
    ackInput?: AckInput, options?: Parameters<typeof apiFetch>[1]): Promise<Incident> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Incident>(getAckIncidentUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(ackInput)
  }
);}





export const getAckIncidentMutationKey = () => ['ackIncident'] as const;

export const getAckIncidentMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ackIncident>>, TError,AckIncidentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof ackIncident>>, TError,AckIncidentMutationVariables, TContext> => {

const mutationKey = getAckIncidentMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof ackIncident>>, AckIncidentMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  ackIncident(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AckIncidentMutationResult = NonNullable<Awaited<ReturnType<typeof ackIncident>>>
    export type AckIncidentMutationBody = AckInput | undefined
    export type AckIncidentMutationError = NotFoundResponse | ConflictResponse
    export type AckIncidentMutationVariables = {id: string;data?: AckInput}

    /**
 * @summary Acknowledge (stops escalation, records who and when)
 */
export const useAckIncident = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof ackIncident>>, TError,AckIncidentMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof ackIncident>>,
        TError,
        AckIncidentMutationVariables,
        TContext
      > => {
      return useMutation(getAckIncidentMutationOptions(options), queryClient);
    }

export const getAddIncidentNoteUrl = (id: string,) => {




  return `/api/v1/incidents/${id}/notes`
}

export const addIncidentNote = async (id: string,
    noteInput: NoteInput, options?: Parameters<typeof apiFetch>[1]): Promise<IncidentEvent> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<IncidentEvent>(getAddIncidentNoteUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(noteInput)
  }
);}





export const getAddIncidentNoteMutationKey = () => ['addIncidentNote'] as const;

export const getAddIncidentNoteMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addIncidentNote>>, TError,AddIncidentNoteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addIncidentNote>>, TError,AddIncidentNoteMutationVariables, TContext> => {

const mutationKey = getAddIncidentNoteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addIncidentNote>>, AddIncidentNoteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addIncidentNote(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddIncidentNoteMutationResult = NonNullable<Awaited<ReturnType<typeof addIncidentNote>>>
    export type AddIncidentNoteMutationBody = NoteInput
    export type AddIncidentNoteMutationError = NotFoundResponse | UnprocessableResponse
    export type AddIncidentNoteMutationVariables = {id: string;data: NoteInput}

    export const useAddIncidentNote = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addIncidentNote>>, TError,AddIncidentNoteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addIncidentNote>>,
        TError,
        AddIncidentNoteMutationVariables,
        TContext
      > => {
      return useMutation(getAddIncidentNoteMutationOptions(options), queryClient);
    }

export const getListMaintenanceWindowsUrl = () => {




  return `/api/v1/maintenance-windows`
}

export const listMaintenanceWindows = async ( options?: Parameters<typeof apiFetch>[1]): Promise<MaintenanceWindow[]> => {

  return apiFetch<MaintenanceWindow[]>(getListMaintenanceWindowsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMaintenanceWindowsQueryKey = () => {
    return [
    `/api/v1/maintenance-windows`
    ] as const;
    }


export const getListMaintenanceWindowsQueryOptions = <TData = Awaited<ReturnType<typeof listMaintenanceWindows>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMaintenanceWindowsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMaintenanceWindows>>> = ({ signal }) => listMaintenanceWindows({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMaintenanceWindowsQueryResult = NonNullable<Awaited<ReturnType<typeof listMaintenanceWindows>>>
export type ListMaintenanceWindowsQueryError = unknown


export function useListMaintenanceWindows<TData = Awaited<ReturnType<typeof listMaintenanceWindows>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMaintenanceWindows>>,
          TError,
          Awaited<ReturnType<typeof listMaintenanceWindows>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMaintenanceWindows<TData = Awaited<ReturnType<typeof listMaintenanceWindows>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMaintenanceWindows>>,
          TError,
          Awaited<ReturnType<typeof listMaintenanceWindows>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMaintenanceWindows<TData = Awaited<ReturnType<typeof listMaintenanceWindows>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListMaintenanceWindows<TData = Awaited<ReturnType<typeof listMaintenanceWindows>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMaintenanceWindows>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMaintenanceWindowsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMaintenanceWindowUrl = () => {




  return `/api/v1/maintenance-windows`
}

export const createMaintenanceWindow = async (maintenanceWindowInput: MaintenanceWindowInput, options?: Parameters<typeof apiFetch>[1]): Promise<MaintenanceWindow> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<MaintenanceWindow>(getCreateMaintenanceWindowUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(maintenanceWindowInput)
  }
);}





export const getCreateMaintenanceWindowMutationKey = () => ['createMaintenanceWindow'] as const;

export const getCreateMaintenanceWindowMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMaintenanceWindow>>, TError,CreateMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMaintenanceWindow>>, TError,CreateMaintenanceWindowMutationVariables, TContext> => {

const mutationKey = getCreateMaintenanceWindowMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMaintenanceWindow>>, CreateMaintenanceWindowMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMaintenanceWindow(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMaintenanceWindowMutationResult = NonNullable<Awaited<ReturnType<typeof createMaintenanceWindow>>>
    export type CreateMaintenanceWindowMutationBody = MaintenanceWindowInput
    export type CreateMaintenanceWindowMutationError = UnprocessableResponse
    export type CreateMaintenanceWindowMutationVariables = {data: MaintenanceWindowInput}

    export const useCreateMaintenanceWindow = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMaintenanceWindow>>, TError,CreateMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createMaintenanceWindow>>,
        TError,
        CreateMaintenanceWindowMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMaintenanceWindowMutationOptions(options), queryClient);
    }

export const getGetMaintenanceWindowUrl = (id: string,) => {




  return `/api/v1/maintenance-windows/${id}`
}

export const getMaintenanceWindow = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<MaintenanceWindow> => {

  return apiFetch<MaintenanceWindow>(getGetMaintenanceWindowUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMaintenanceWindowQueryKey = (id: string,) => {
    return [
    `/api/v1/maintenance-windows/${id}`
    ] as const;
    }


export const getGetMaintenanceWindowQueryOptions = <TData = Awaited<ReturnType<typeof getMaintenanceWindow>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMaintenanceWindowQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMaintenanceWindow>>> = ({ signal }) => getMaintenanceWindow(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMaintenanceWindowQueryResult = NonNullable<Awaited<ReturnType<typeof getMaintenanceWindow>>>
export type GetMaintenanceWindowQueryError = NotFoundResponse


export function useGetMaintenanceWindow<TData = Awaited<ReturnType<typeof getMaintenanceWindow>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMaintenanceWindow>>,
          TError,
          Awaited<ReturnType<typeof getMaintenanceWindow>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMaintenanceWindow<TData = Awaited<ReturnType<typeof getMaintenanceWindow>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMaintenanceWindow>>,
          TError,
          Awaited<ReturnType<typeof getMaintenanceWindow>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMaintenanceWindow<TData = Awaited<ReturnType<typeof getMaintenanceWindow>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetMaintenanceWindow<TData = Awaited<ReturnType<typeof getMaintenanceWindow>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMaintenanceWindow>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMaintenanceWindowQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateMaintenanceWindowUrl = (id: string,) => {




  return `/api/v1/maintenance-windows/${id}`
}

export const updateMaintenanceWindow = async (id: string,
    maintenanceWindowInput: MaintenanceWindowInput, options?: Parameters<typeof apiFetch>[1]): Promise<MaintenanceWindow> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<MaintenanceWindow>(getUpdateMaintenanceWindowUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(maintenanceWindowInput)
  }
);}





export const getUpdateMaintenanceWindowMutationKey = () => ['updateMaintenanceWindow'] as const;

export const getUpdateMaintenanceWindowMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMaintenanceWindow>>, TError,UpdateMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateMaintenanceWindow>>, TError,UpdateMaintenanceWindowMutationVariables, TContext> => {

const mutationKey = getUpdateMaintenanceWindowMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateMaintenanceWindow>>, UpdateMaintenanceWindowMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateMaintenanceWindow(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateMaintenanceWindowMutationResult = NonNullable<Awaited<ReturnType<typeof updateMaintenanceWindow>>>
    export type UpdateMaintenanceWindowMutationBody = MaintenanceWindowInput
    export type UpdateMaintenanceWindowMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateMaintenanceWindowMutationVariables = {id: string;data: MaintenanceWindowInput}

    export const useUpdateMaintenanceWindow = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMaintenanceWindow>>, TError,UpdateMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateMaintenanceWindow>>,
        TError,
        UpdateMaintenanceWindowMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateMaintenanceWindowMutationOptions(options), queryClient);
    }

export const getDeleteMaintenanceWindowUrl = (id: string,) => {




  return `/api/v1/maintenance-windows/${id}`
}

export const deleteMaintenanceWindow = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteMaintenanceWindowUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteMaintenanceWindowMutationKey = () => ['deleteMaintenanceWindow'] as const;

export const getDeleteMaintenanceWindowMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMaintenanceWindow>>, TError,DeleteMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteMaintenanceWindow>>, TError,DeleteMaintenanceWindowMutationVariables, TContext> => {

const mutationKey = getDeleteMaintenanceWindowMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteMaintenanceWindow>>, DeleteMaintenanceWindowMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteMaintenanceWindow(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteMaintenanceWindowMutationResult = NonNullable<Awaited<ReturnType<typeof deleteMaintenanceWindow>>>

    export type DeleteMaintenanceWindowMutationError = NotFoundResponse
    export type DeleteMaintenanceWindowMutationVariables = {id: string}

    export const useDeleteMaintenanceWindow = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMaintenanceWindow>>, TError,DeleteMaintenanceWindowMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteMaintenanceWindow>>,
        TError,
        DeleteMaintenanceWindowMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteMaintenanceWindowMutationOptions(options), queryClient);
    }

export const getListNotificationChannelsUrl = () => {




  return `/api/v1/notification-channels`
}

export const listNotificationChannels = async ( options?: Parameters<typeof apiFetch>[1]): Promise<NotificationChannel[]> => {

  return apiFetch<NotificationChannel[]>(getListNotificationChannelsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNotificationChannelsQueryKey = () => {
    return [
    `/api/v1/notification-channels`
    ] as const;
    }


export const getListNotificationChannelsQueryOptions = <TData = Awaited<ReturnType<typeof listNotificationChannels>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNotificationChannelsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotificationChannels>>> = ({ signal }) => listNotificationChannels({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNotificationChannelsQueryResult = NonNullable<Awaited<ReturnType<typeof listNotificationChannels>>>
export type ListNotificationChannelsQueryError = unknown


export function useListNotificationChannels<TData = Awaited<ReturnType<typeof listNotificationChannels>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotificationChannels>>,
          TError,
          Awaited<ReturnType<typeof listNotificationChannels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotificationChannels<TData = Awaited<ReturnType<typeof listNotificationChannels>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotificationChannels>>,
          TError,
          Awaited<ReturnType<typeof listNotificationChannels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotificationChannels<TData = Awaited<ReturnType<typeof listNotificationChannels>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListNotificationChannels<TData = Awaited<ReturnType<typeof listNotificationChannels>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNotificationChannelsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateNotificationChannelUrl = () => {




  return `/api/v1/notification-channels`
}

export const createNotificationChannel = async (notificationChannelInput: NotificationChannelInput, options?: Parameters<typeof apiFetch>[1]): Promise<NotificationChannel> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<NotificationChannel>(getCreateNotificationChannelUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(notificationChannelInput)
  }
);}





export const getCreateNotificationChannelMutationKey = () => ['createNotificationChannel'] as const;

export const getCreateNotificationChannelMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotificationChannel>>, TError,CreateNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createNotificationChannel>>, TError,CreateNotificationChannelMutationVariables, TContext> => {

const mutationKey = getCreateNotificationChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createNotificationChannel>>, CreateNotificationChannelMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createNotificationChannel(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateNotificationChannelMutationResult = NonNullable<Awaited<ReturnType<typeof createNotificationChannel>>>
    export type CreateNotificationChannelMutationBody = NotificationChannelInput
    export type CreateNotificationChannelMutationError = UnprocessableResponse
    export type CreateNotificationChannelMutationVariables = {data: NotificationChannelInput}

    export const useCreateNotificationChannel = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotificationChannel>>, TError,CreateNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createNotificationChannel>>,
        TError,
        CreateNotificationChannelMutationVariables,
        TContext
      > => {
      return useMutation(getCreateNotificationChannelMutationOptions(options), queryClient);
    }

export const getTestNotificationConfigUrl = () => {




  return `/api/v1/notification-channels/test`
}

/**
 * @summary Send a test message with unsaved settings
 */
export const testNotificationConfig = async (notificationChannelInput: NotificationChannelInput, options?: Parameters<typeof apiFetch>[1]): Promise<TestResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<TestResult>(getTestNotificationConfigUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(notificationChannelInput)
  }
);}





export const getTestNotificationConfigMutationKey = () => ['testNotificationConfig'] as const;

export const getTestNotificationConfigMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testNotificationConfig>>, TError,TestNotificationConfigMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testNotificationConfig>>, TError,TestNotificationConfigMutationVariables, TContext> => {

const mutationKey = getTestNotificationConfigMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testNotificationConfig>>, TestNotificationConfigMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  testNotificationConfig(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestNotificationConfigMutationResult = NonNullable<Awaited<ReturnType<typeof testNotificationConfig>>>
    export type TestNotificationConfigMutationBody = NotificationChannelInput
    export type TestNotificationConfigMutationError = UnprocessableResponse
    export type TestNotificationConfigMutationVariables = {data: NotificationChannelInput}

    /**
 * @summary Send a test message with unsaved settings
 */
export const useTestNotificationConfig = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testNotificationConfig>>, TError,TestNotificationConfigMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof testNotificationConfig>>,
        TError,
        TestNotificationConfigMutationVariables,
        TContext
      > => {
      return useMutation(getTestNotificationConfigMutationOptions(options), queryClient);
    }

export const getUpdateNotificationChannelUrl = (id: string,) => {




  return `/api/v1/notification-channels/${id}`
}

/**
 * @summary Replace settings; omitted secret fields keep their stored value
 */
export const updateNotificationChannel = async (id: string,
    notificationChannelInput: NotificationChannelInput, options?: Parameters<typeof apiFetch>[1]): Promise<NotificationChannel> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<NotificationChannel>(getUpdateNotificationChannelUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(notificationChannelInput)
  }
);}





export const getUpdateNotificationChannelMutationKey = () => ['updateNotificationChannel'] as const;

export const getUpdateNotificationChannelMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationChannel>>, TError,UpdateNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNotificationChannel>>, TError,UpdateNotificationChannelMutationVariables, TContext> => {

const mutationKey = getUpdateNotificationChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNotificationChannel>>, UpdateNotificationChannelMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateNotificationChannel(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNotificationChannelMutationResult = NonNullable<Awaited<ReturnType<typeof updateNotificationChannel>>>
    export type UpdateNotificationChannelMutationBody = NotificationChannelInput
    export type UpdateNotificationChannelMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateNotificationChannelMutationVariables = {id: string;data: NotificationChannelInput}

    /**
 * @summary Replace settings; omitted secret fields keep their stored value
 */
export const useUpdateNotificationChannel = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationChannel>>, TError,UpdateNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNotificationChannel>>,
        TError,
        UpdateNotificationChannelMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateNotificationChannelMutationOptions(options), queryClient);
    }

export const getDeleteNotificationChannelUrl = (id: string,) => {




  return `/api/v1/notification-channels/${id}`
}

export const deleteNotificationChannel = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteNotificationChannelUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteNotificationChannelMutationKey = () => ['deleteNotificationChannel'] as const;

export const getDeleteNotificationChannelMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationChannel>>, TError,DeleteNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationChannel>>, TError,DeleteNotificationChannelMutationVariables, TContext> => {

const mutationKey = getDeleteNotificationChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNotificationChannel>>, DeleteNotificationChannelMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteNotificationChannel(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNotificationChannelMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNotificationChannel>>>

    export type DeleteNotificationChannelMutationError = NotFoundResponse
    export type DeleteNotificationChannelMutationVariables = {id: string}

    export const useDeleteNotificationChannel = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationChannel>>, TError,DeleteNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNotificationChannel>>,
        TError,
        DeleteNotificationChannelMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteNotificationChannelMutationOptions(options), queryClient);
    }

export const getTestNotificationChannelUrl = (id: string,) => {




  return `/api/v1/notification-channels/${id}/test`
}

export const testNotificationChannel = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<TestResult> => {

  return apiFetch<TestResult>(getTestNotificationChannelUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getTestNotificationChannelMutationKey = () => ['testNotificationChannel'] as const;

export const getTestNotificationChannelMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testNotificationChannel>>, TError,TestNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testNotificationChannel>>, TError,TestNotificationChannelMutationVariables, TContext> => {

const mutationKey = getTestNotificationChannelMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testNotificationChannel>>, TestNotificationChannelMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  testNotificationChannel(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestNotificationChannelMutationResult = NonNullable<Awaited<ReturnType<typeof testNotificationChannel>>>

    export type TestNotificationChannelMutationError = NotFoundResponse
    export type TestNotificationChannelMutationVariables = {id: string}

    export const useTestNotificationChannel = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testNotificationChannel>>, TError,TestNotificationChannelMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof testNotificationChannel>>,
        TError,
        TestNotificationChannelMutationVariables,
        TContext
      > => {
      return useMutation(getTestNotificationChannelMutationOptions(options), queryClient);
    }

export const getListNotificationRoutesUrl = () => {




  return `/api/v1/notification-routes`
}

export const listNotificationRoutes = async ( options?: Parameters<typeof apiFetch>[1]): Promise<NotificationRoute[]> => {

  return apiFetch<NotificationRoute[]>(getListNotificationRoutesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNotificationRoutesQueryKey = () => {
    return [
    `/api/v1/notification-routes`
    ] as const;
    }


export const getListNotificationRoutesQueryOptions = <TData = Awaited<ReturnType<typeof listNotificationRoutes>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNotificationRoutesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotificationRoutes>>> = ({ signal }) => listNotificationRoutes({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNotificationRoutesQueryResult = NonNullable<Awaited<ReturnType<typeof listNotificationRoutes>>>
export type ListNotificationRoutesQueryError = unknown


export function useListNotificationRoutes<TData = Awaited<ReturnType<typeof listNotificationRoutes>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotificationRoutes>>,
          TError,
          Awaited<ReturnType<typeof listNotificationRoutes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotificationRoutes<TData = Awaited<ReturnType<typeof listNotificationRoutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotificationRoutes>>,
          TError,
          Awaited<ReturnType<typeof listNotificationRoutes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNotificationRoutes<TData = Awaited<ReturnType<typeof listNotificationRoutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListNotificationRoutes<TData = Awaited<ReturnType<typeof listNotificationRoutes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNotificationRoutes>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNotificationRoutesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateNotificationRouteUrl = () => {




  return `/api/v1/notification-routes`
}

export const createNotificationRoute = async (notificationRouteInput: NotificationRouteInput, options?: Parameters<typeof apiFetch>[1]): Promise<NotificationRoute> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<NotificationRoute>(getCreateNotificationRouteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(notificationRouteInput)
  }
);}





export const getCreateNotificationRouteMutationKey = () => ['createNotificationRoute'] as const;

export const getCreateNotificationRouteMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotificationRoute>>, TError,CreateNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createNotificationRoute>>, TError,CreateNotificationRouteMutationVariables, TContext> => {

const mutationKey = getCreateNotificationRouteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createNotificationRoute>>, CreateNotificationRouteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createNotificationRoute(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateNotificationRouteMutationResult = NonNullable<Awaited<ReturnType<typeof createNotificationRoute>>>
    export type CreateNotificationRouteMutationBody = NotificationRouteInput
    export type CreateNotificationRouteMutationError = UnprocessableResponse
    export type CreateNotificationRouteMutationVariables = {data: NotificationRouteInput}

    export const useCreateNotificationRoute = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createNotificationRoute>>, TError,CreateNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createNotificationRoute>>,
        TError,
        CreateNotificationRouteMutationVariables,
        TContext
      > => {
      return useMutation(getCreateNotificationRouteMutationOptions(options), queryClient);
    }

export const getUpdateNotificationRouteUrl = (id: string,) => {




  return `/api/v1/notification-routes/${id}`
}

export const updateNotificationRoute = async (id: string,
    notificationRouteInput: NotificationRouteInput, options?: Parameters<typeof apiFetch>[1]): Promise<NotificationRoute> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<NotificationRoute>(getUpdateNotificationRouteUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(notificationRouteInput)
  }
);}





export const getUpdateNotificationRouteMutationKey = () => ['updateNotificationRoute'] as const;

export const getUpdateNotificationRouteMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationRoute>>, TError,UpdateNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNotificationRoute>>, TError,UpdateNotificationRouteMutationVariables, TContext> => {

const mutationKey = getUpdateNotificationRouteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNotificationRoute>>, UpdateNotificationRouteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateNotificationRoute(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNotificationRouteMutationResult = NonNullable<Awaited<ReturnType<typeof updateNotificationRoute>>>
    export type UpdateNotificationRouteMutationBody = NotificationRouteInput
    export type UpdateNotificationRouteMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateNotificationRouteMutationVariables = {id: string;data: NotificationRouteInput}

    export const useUpdateNotificationRoute = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNotificationRoute>>, TError,UpdateNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNotificationRoute>>,
        TError,
        UpdateNotificationRouteMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateNotificationRouteMutationOptions(options), queryClient);
    }

export const getDeleteNotificationRouteUrl = (id: string,) => {




  return `/api/v1/notification-routes/${id}`
}

export const deleteNotificationRoute = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteNotificationRouteUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteNotificationRouteMutationKey = () => ['deleteNotificationRoute'] as const;

export const getDeleteNotificationRouteMutationOptions = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationRoute>>, TError,DeleteNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationRoute>>, TError,DeleteNotificationRouteMutationVariables, TContext> => {

const mutationKey = getDeleteNotificationRouteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNotificationRoute>>, DeleteNotificationRouteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteNotificationRoute(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNotificationRouteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNotificationRoute>>>

    export type DeleteNotificationRouteMutationError = NotFoundResponse
    export type DeleteNotificationRouteMutationVariables = {id: string}

    export const useDeleteNotificationRoute = <TError = NotFoundResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNotificationRoute>>, TError,DeleteNotificationRouteMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNotificationRoute>>,
        TError,
        DeleteNotificationRouteMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteNotificationRouteMutationOptions(options), queryClient);
    }

export const getListUsersUrl = () => {




  return `/api/v1/users`
}

export const listUsers = async ( options?: Parameters<typeof apiFetch>[1]): Promise<User[]> => {

  return apiFetch<User[]>(getListUsersUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListUsersQueryKey = () => {
    return [
    `/api/v1/users`
    ] as const;
    }


export const getListUsersQueryOptions = <TData = Awaited<ReturnType<typeof listUsers>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListUsersQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listUsers>>> = ({ signal }) => listUsers({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listUsers>>>
export type ListUsersQueryError = unknown


export function useListUsers<TData = Awaited<ReturnType<typeof listUsers>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsers>>,
          TError,
          Awaited<ReturnType<typeof listUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListUsers<TData = Awaited<ReturnType<typeof listUsers>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUsers>>,
          TError,
          Awaited<ReturnType<typeof listUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListUsers<TData = Awaited<ReturnType<typeof listUsers>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListUsers<TData = Awaited<ReturnType<typeof listUsers>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUsers>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListUsersQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateUserUrl = () => {




  return `/api/v1/users`
}

export const createUser = async (userCreate: UserCreate, options?: Parameters<typeof apiFetch>[1]): Promise<User> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<User>(getCreateUserUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(userCreate)
  }
);}





export const getCreateUserMutationKey = () => ['createUser'] as const;

export const getCreateUserMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createUser>>, TError,CreateUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createUser>>, TError,CreateUserMutationVariables, TContext> => {

const mutationKey = getCreateUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createUser>>, CreateUserMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createUser(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateUserMutationResult = NonNullable<Awaited<ReturnType<typeof createUser>>>
    export type CreateUserMutationBody = UserCreate
    export type CreateUserMutationError = ConflictResponse | UnprocessableResponse
    export type CreateUserMutationVariables = {data: UserCreate}

    export const useCreateUser = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createUser>>, TError,CreateUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createUser>>,
        TError,
        CreateUserMutationVariables,
        TContext
      > => {
      return useMutation(getCreateUserMutationOptions(options), queryClient);
    }

export const getUpdateUserUrl = (id: string,) => {




  return `/api/v1/users/${id}`
}

/**
 * @summary Update a user; a role change, deactivation or new password signs them out everywhere
 */
export const updateUser = async (id: string,
    userUpdate: UserUpdate, options?: Parameters<typeof apiFetch>[1]): Promise<User> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<User>(getUpdateUserUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(userUpdate)
  }
);}





export const getUpdateUserMutationKey = () => ['updateUser'] as const;

export const getUpdateUserMutationOptions = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,UpdateUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,UpdateUserMutationVariables, TContext> => {

const mutationKey = getUpdateUserMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateUser>>, UpdateUserMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateUser(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateUserMutationResult = NonNullable<Awaited<ReturnType<typeof updateUser>>>
    export type UpdateUserMutationBody = UserUpdate
    export type UpdateUserMutationError = NotFoundResponse | UnprocessableResponse
    export type UpdateUserMutationVariables = {id: string;data: UserUpdate}

    /**
 * @summary Update a user; a role change, deactivation or new password signs them out everywhere
 */
export const useUpdateUser = <TError = NotFoundResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUser>>, TError,UpdateUserMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateUser>>,
        TError,
        UpdateUserMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateUserMutationOptions(options), queryClient);
    }

export const getGetThresholdSettingsUrl = () => {




  return `/api/v1/settings/thresholds`
}

export const getThresholdSettings = async ( options?: Parameters<typeof apiFetch>[1]): Promise<ThresholdSettings> => {

  return apiFetch<ThresholdSettings>(getGetThresholdSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetThresholdSettingsQueryKey = () => {
    return [
    `/api/v1/settings/thresholds`
    ] as const;
    }


export const getGetThresholdSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getThresholdSettings>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetThresholdSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getThresholdSettings>>> = ({ signal }) => getThresholdSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetThresholdSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getThresholdSettings>>>
export type GetThresholdSettingsQueryError = unknown


export function useGetThresholdSettings<TData = Awaited<ReturnType<typeof getThresholdSettings>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getThresholdSettings>>,
          TError,
          Awaited<ReturnType<typeof getThresholdSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetThresholdSettings<TData = Awaited<ReturnType<typeof getThresholdSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getThresholdSettings>>,
          TError,
          Awaited<ReturnType<typeof getThresholdSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetThresholdSettings<TData = Awaited<ReturnType<typeof getThresholdSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetThresholdSettings<TData = Awaited<ReturnType<typeof getThresholdSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getThresholdSettings>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetThresholdSettingsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateThresholdSettingsUrl = () => {




  return `/api/v1/settings/thresholds`
}

export const updateThresholdSettings = async (thresholdSettings: ThresholdSettings, options?: Parameters<typeof apiFetch>[1]): Promise<ThresholdSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<ThresholdSettings>(getUpdateThresholdSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(thresholdSettings)
  }
);}





export const getUpdateThresholdSettingsMutationKey = () => ['updateThresholdSettings'] as const;

export const getUpdateThresholdSettingsMutationOptions = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateThresholdSettings>>, TError,UpdateThresholdSettingsMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateThresholdSettings>>, TError,UpdateThresholdSettingsMutationVariables, TContext> => {

const mutationKey = getUpdateThresholdSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateThresholdSettings>>, UpdateThresholdSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  updateThresholdSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateThresholdSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof updateThresholdSettings>>>
    export type UpdateThresholdSettingsMutationBody = ThresholdSettings
    export type UpdateThresholdSettingsMutationError = UnprocessableResponse
    export type UpdateThresholdSettingsMutationVariables = {data: ThresholdSettings}

    export const useUpdateThresholdSettings = <TError = UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateThresholdSettings>>, TError,UpdateThresholdSettingsMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateThresholdSettings>>,
        TError,
        UpdateThresholdSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateThresholdSettingsMutationOptions(options), queryClient);
    }

export const getListAuditLogUrl = (params?: ListAuditLogParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/audit-log?${stringifiedParams}` : `/api/v1/audit-log`
}

export const listAuditLog = async (params?: ListAuditLogParams, options?: Parameters<typeof apiFetch>[1]): Promise<AuditEntry[]> => {

  return apiFetch<AuditEntry[]>(getListAuditLogUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAuditLogQueryKey = (params?: ListAuditLogParams,) => {
    return [
    `/api/v1/audit-log`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAuditLogQueryOptions = <TData = Awaited<ReturnType<typeof listAuditLog>>, TError = unknown>(params?: ListAuditLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAuditLogQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAuditLog>>> = ({ signal }) => listAuditLog(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAuditLogQueryResult = NonNullable<Awaited<ReturnType<typeof listAuditLog>>>
export type ListAuditLogQueryError = unknown


export function useListAuditLog<TData = Awaited<ReturnType<typeof listAuditLog>>, TError = unknown>(
 params: undefined |  ListAuditLogParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuditLog>>,
          TError,
          Awaited<ReturnType<typeof listAuditLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAuditLog<TData = Awaited<ReturnType<typeof listAuditLog>>, TError = unknown>(
 params?: ListAuditLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuditLog>>,
          TError,
          Awaited<ReturnType<typeof listAuditLog>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAuditLog<TData = Awaited<ReturnType<typeof listAuditLog>>, TError = unknown>(
 params?: ListAuditLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListAuditLog<TData = Awaited<ReturnType<typeof listAuditLog>>, TError = unknown>(
 params?: ListAuditLogParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditLog>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAuditLogQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAdhocRequestUrl = (id: string,) => {




  return `/api/v1/probes/${id}/adhoc`
}

/**
 * @summary Run a one-off check or traceroute from this probe
 */
export const createAdhocRequest = async (id: string,
    adhocInput: AdhocInput, options?: Parameters<typeof apiFetch>[1]): Promise<AdhocRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<AdhocRequest>(getCreateAdhocRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adhocInput)
  }
);}





export const getCreateAdhocRequestMutationKey = () => ['createAdhocRequest'] as const;

export const getCreateAdhocRequestMutationOptions = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdhocRequest>>, TError,CreateAdhocRequestMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAdhocRequest>>, TError,CreateAdhocRequestMutationVariables, TContext> => {

const mutationKey = getCreateAdhocRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAdhocRequest>>, CreateAdhocRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createAdhocRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAdhocRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createAdhocRequest>>>
    export type CreateAdhocRequestMutationBody = AdhocInput
    export type CreateAdhocRequestMutationError = NotFoundResponse | ConflictResponse | UnprocessableResponse
    export type CreateAdhocRequestMutationVariables = {id: string;data: AdhocInput}

    /**
 * @summary Run a one-off check or traceroute from this probe
 */
export const useCreateAdhocRequest = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAdhocRequest>>, TError,CreateAdhocRequestMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createAdhocRequest>>,
        TError,
        CreateAdhocRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAdhocRequestMutationOptions(options), queryClient);
    }

export const getGetBgpStatusUrl = () => {




  return `/api/v1/bgp`
}

/**
 * docs/adr/2026-10-04-as-path-monitoring.md §6–§7. Internal only (§8): RIS and RIPEstat data. Disabled when ATLAS_BGP_ASNS is empty.
 * @summary The BGP watch through the RIPE RIS collectors
 */
export const getBgpStatus = async ( options?: Parameters<typeof apiFetch>[1]): Promise<BgpStatus> => {

  return apiFetch<BgpStatus>(getGetBgpStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBgpStatusQueryKey = () => {
    return [
    `/api/v1/bgp`
    ] as const;
    }


export const getGetBgpStatusQueryOptions = <TData = Awaited<ReturnType<typeof getBgpStatus>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBgpStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBgpStatus>>> = ({ signal }) => getBgpStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBgpStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getBgpStatus>>>
export type GetBgpStatusQueryError = unknown


export function useGetBgpStatus<TData = Awaited<ReturnType<typeof getBgpStatus>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBgpStatus>>,
          TError,
          Awaited<ReturnType<typeof getBgpStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBgpStatus<TData = Awaited<ReturnType<typeof getBgpStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBgpStatus>>,
          TError,
          Awaited<ReturnType<typeof getBgpStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBgpStatus<TData = Awaited<ReturnType<typeof getBgpStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The BGP watch through the RIPE RIS collectors
 */

export function useGetBgpStatus<TData = Awaited<ReturnType<typeof getBgpStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBgpStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBgpStatusQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBgpPrefixUrl = () => {




  return `/api/v1/bgp/prefixes`
}

/**
 * docs/adr/2026-10-04-external-prefix-watches.md §1–§2. At most 20 external prefixes; IPv4 /16 to /24, IPv6 /32 to /48.
 * @summary Watch an external prefix
 */
export const createBgpPrefix = async (bgpPrefixInput: BgpPrefixInput, options?: Parameters<typeof apiFetch>[1]): Promise<BgpPrefixConfig> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<BgpPrefixConfig>(getCreateBgpPrefixUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bgpPrefixInput)
  }
);}





export const getCreateBgpPrefixMutationKey = () => ['createBgpPrefix'] as const;

export const getCreateBgpPrefixMutationOptions = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBgpPrefix>>, TError,CreateBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBgpPrefix>>, TError,CreateBgpPrefixMutationVariables, TContext> => {

const mutationKey = getCreateBgpPrefixMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBgpPrefix>>, CreateBgpPrefixMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBgpPrefix(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBgpPrefixMutationResult = NonNullable<Awaited<ReturnType<typeof createBgpPrefix>>>
    export type CreateBgpPrefixMutationBody = BgpPrefixInput
    export type CreateBgpPrefixMutationError = ConflictResponse | UnprocessableResponse
    export type CreateBgpPrefixMutationVariables = {data: BgpPrefixInput}

    /**
 * @summary Watch an external prefix
 */
export const useCreateBgpPrefix = <TError = ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBgpPrefix>>, TError,CreateBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBgpPrefix>>,
        TError,
        CreateBgpPrefixMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBgpPrefixMutationOptions(options), queryClient);
    }

export const getUpdateBgpPrefixUrl = (id: string,) => {




  return `/api/v1/bgp/prefixes/${id}`
}

/**
 * @summary Change an external prefix's expected origins and labels
 */
export const updateBgpPrefix = async (id: string,
    bgpPrefixUpdate: BgpPrefixUpdate, options?: Parameters<typeof apiFetch>[1]): Promise<BgpPrefixConfig> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<BgpPrefixConfig>(getUpdateBgpPrefixUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(bgpPrefixUpdate)
  }
);}





export const getUpdateBgpPrefixMutationKey = () => ['updateBgpPrefix'] as const;

export const getUpdateBgpPrefixMutationOptions = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBgpPrefix>>, TError,UpdateBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBgpPrefix>>, TError,UpdateBgpPrefixMutationVariables, TContext> => {

const mutationKey = getUpdateBgpPrefixMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBgpPrefix>>, UpdateBgpPrefixMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBgpPrefix(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBgpPrefixMutationResult = NonNullable<Awaited<ReturnType<typeof updateBgpPrefix>>>
    export type UpdateBgpPrefixMutationBody = BgpPrefixUpdate
    export type UpdateBgpPrefixMutationError = NotFoundResponse | ConflictResponse | UnprocessableResponse
    export type UpdateBgpPrefixMutationVariables = {id: string;data: BgpPrefixUpdate}

    /**
 * @summary Change an external prefix's expected origins and labels
 */
export const useUpdateBgpPrefix = <TError = NotFoundResponse | ConflictResponse | UnprocessableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBgpPrefix>>, TError,UpdateBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateBgpPrefix>>,
        TError,
        UpdateBgpPrefixMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBgpPrefixMutationOptions(options), queryClient);
    }

export const getDeleteBgpPrefixUrl = (id: string,) => {




  return `/api/v1/bgp/prefixes/${id}`
}

/**
 * @summary Stop watching an external prefix (its incidents and maintenance windows go with it)
 */
export const deleteBgpPrefix = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteBgpPrefixUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBgpPrefixMutationKey = () => ['deleteBgpPrefix'] as const;

export const getDeleteBgpPrefixMutationOptions = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBgpPrefix>>, TError,DeleteBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBgpPrefix>>, TError,DeleteBgpPrefixMutationVariables, TContext> => {

const mutationKey = getDeleteBgpPrefixMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBgpPrefix>>, DeleteBgpPrefixMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBgpPrefix(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBgpPrefixMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBgpPrefix>>>

    export type DeleteBgpPrefixMutationError = NotFoundResponse | ConflictResponse
    export type DeleteBgpPrefixMutationVariables = {id: string}

    /**
 * @summary Stop watching an external prefix (its incidents and maintenance windows go with it)
 */
export const useDeleteBgpPrefix = <TError = NotFoundResponse | ConflictResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBgpPrefix>>, TError,DeleteBgpPrefixMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteBgpPrefix>>,
        TError,
        DeleteBgpPrefixMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBgpPrefixMutationOptions(options), queryClient);
    }

export const getListBgpPrefixPathsUrl = (id: string,) => {




  return `/api/v1/bgp/prefixes/${id}/paths`
}

/**
 * @summary The distinct AS paths the collectors' full-table peers hold for a watched prefix (most used first, max 30)
 */
export const listBgpPrefixPaths = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<BgpPaths> => {

  return apiFetch<BgpPaths>(getListBgpPrefixPathsUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBgpPrefixPathsQueryKey = (id: string,) => {
    return [
    `/api/v1/bgp/prefixes/${id}/paths`
    ] as const;
    }


export const getListBgpPrefixPathsQueryOptions = <TData = Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBgpPrefixPathsQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBgpPrefixPaths>>> = ({ signal }) => listBgpPrefixPaths(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBgpPrefixPathsQueryResult = NonNullable<Awaited<ReturnType<typeof listBgpPrefixPaths>>>
export type ListBgpPrefixPathsQueryError = NotFoundResponse


export function useListBgpPrefixPaths<TData = Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBgpPrefixPaths>>,
          TError,
          Awaited<ReturnType<typeof listBgpPrefixPaths>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBgpPrefixPaths<TData = Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBgpPrefixPaths>>,
          TError,
          Awaited<ReturnType<typeof listBgpPrefixPaths>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBgpPrefixPaths<TData = Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The distinct AS paths the collectors' full-table peers hold for a watched prefix (most used first, max 30)
 */

export function useListBgpPrefixPaths<TData = Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpPrefixPaths>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBgpPrefixPathsQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListBgpUpdatesUrl = (id: string,
    params?: ListBgpUpdatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/bgp/prefixes/${id}/updates?${stringifiedParams}` : `/api/v1/bgp/prefixes/${id}/updates`
}

/**
 * @summary BGP updates for a watched prefix and its more-specifics (newest first, max 500)
 */
export const listBgpUpdates = async (id: string,
    params?: ListBgpUpdatesParams, options?: Parameters<typeof apiFetch>[1]): Promise<BgpUpdate[]> => {

  return apiFetch<BgpUpdate[]>(getListBgpUpdatesUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBgpUpdatesQueryKey = (id: string,
    params?: ListBgpUpdatesParams,) => {
    return [
    `/api/v1/bgp/prefixes/${id}/updates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBgpUpdatesQueryOptions = <TData = Awaited<ReturnType<typeof listBgpUpdates>>, TError = NotFoundResponse>(id: string,
    params?: ListBgpUpdatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBgpUpdatesQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBgpUpdates>>> = ({ signal }) => listBgpUpdates(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBgpUpdatesQueryResult = NonNullable<Awaited<ReturnType<typeof listBgpUpdates>>>
export type ListBgpUpdatesQueryError = NotFoundResponse


export function useListBgpUpdates<TData = Awaited<ReturnType<typeof listBgpUpdates>>, TError = NotFoundResponse>(
 id: string,
    params: undefined |  ListBgpUpdatesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBgpUpdates>>,
          TError,
          Awaited<ReturnType<typeof listBgpUpdates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBgpUpdates<TData = Awaited<ReturnType<typeof listBgpUpdates>>, TError = NotFoundResponse>(
 id: string,
    params?: ListBgpUpdatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBgpUpdates>>,
          TError,
          Awaited<ReturnType<typeof listBgpUpdates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBgpUpdates<TData = Awaited<ReturnType<typeof listBgpUpdates>>, TError = NotFoundResponse>(
 id: string,
    params?: ListBgpUpdatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary BGP updates for a watched prefix and its more-specifics (newest first, max 500)
 */

export function useListBgpUpdates<TData = Awaited<ReturnType<typeof listBgpUpdates>>, TError = NotFoundResponse>(
 id: string,
    params?: ListBgpUpdatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBgpUpdates>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBgpUpdatesQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAdhocRequestUrl = (id: string,) => {




  return `/api/v1/adhoc-requests/${id}`
}

export const getAdhocRequest = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<AdhocRequest> => {

  return apiFetch<AdhocRequest>(getGetAdhocRequestUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdhocRequestQueryKey = (id: string,) => {
    return [
    `/api/v1/adhoc-requests/${id}`
    ] as const;
    }


export const getGetAdhocRequestQueryOptions = <TData = Awaited<ReturnType<typeof getAdhocRequest>>, TError = NotFoundResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdhocRequestQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdhocRequest>>> = ({ signal }) => getAdhocRequest(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAdhocRequestQueryResult = NonNullable<Awaited<ReturnType<typeof getAdhocRequest>>>
export type GetAdhocRequestQueryError = NotFoundResponse


export function useGetAdhocRequest<TData = Awaited<ReturnType<typeof getAdhocRequest>>, TError = NotFoundResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdhocRequest>>,
          TError,
          Awaited<ReturnType<typeof getAdhocRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdhocRequest<TData = Awaited<ReturnType<typeof getAdhocRequest>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdhocRequest>>,
          TError,
          Awaited<ReturnType<typeof getAdhocRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdhocRequest<TData = Awaited<ReturnType<typeof getAdhocRequest>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAdhocRequest<TData = Awaited<ReturnType<typeof getAdhocRequest>>, TError = NotFoundResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdhocRequest>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAdhocRequestQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSlaReportUrl = (params: GetSlaReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/reports/sla?${stringifiedParams}` : `/api/v1/reports/sla`
}

/**
 * Availability per link and monitor for one calendar month, from the status history (docs/adr/2026-09-28-sla-reports.md). Customers get their tenant's report. Admins and NOC users get every link and monitor, or with tenant_id exactly the report that tenant's customers see.
 * @summary Monthly SLA report (PRD §5.10)
 */
export const getSlaReport = async (params: GetSlaReportParams, options?: Parameters<typeof apiFetch>[1]): Promise<SlaReport> => {

  return apiFetch<SlaReport>(getGetSlaReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSlaReportQueryKey = (params?: GetSlaReportParams,) => {
    return [
    `/api/v1/reports/sla`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetSlaReportQueryOptions = <TData = Awaited<ReturnType<typeof getSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(params: GetSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSlaReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSlaReport>>> = ({ signal }) => getSlaReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSlaReportQueryResult = NonNullable<Awaited<ReturnType<typeof getSlaReport>>>
export type GetSlaReportQueryError = NotFoundResponse | UnprocessableResponse


export function useGetSlaReport<TData = Awaited<ReturnType<typeof getSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: GetSlaReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSlaReport>>,
          TError,
          Awaited<ReturnType<typeof getSlaReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSlaReport<TData = Awaited<ReturnType<typeof getSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: GetSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSlaReport>>,
          TError,
          Awaited<ReturnType<typeof getSlaReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSlaReport<TData = Awaited<ReturnType<typeof getSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: GetSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Monthly SLA report (PRD §5.10)
 */

export function useGetSlaReport<TData = Awaited<ReturnType<typeof getSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: GetSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSlaReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportSlaReportUrl = (params: ExportSlaReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/v1/reports/sla/export?${stringifiedParams}` : `/api/v1/reports/sla/export`
}

/**
 * @summary The monthly SLA report as a CSV or PDF download
 */
export const exportSlaReport = async (params: ExportSlaReportParams, options?: Parameters<typeof apiFetch>[1]): Promise<Blob> => {

  return apiFetch<Blob>(getExportSlaReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportSlaReportQueryKey = (params?: ExportSlaReportParams,) => {
    return [
    `/api/v1/reports/sla/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportSlaReportQueryOptions = <TData = Awaited<ReturnType<typeof exportSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(params: ExportSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportSlaReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportSlaReport>>> = ({ signal }) => exportSlaReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportSlaReportQueryResult = NonNullable<Awaited<ReturnType<typeof exportSlaReport>>>
export type ExportSlaReportQueryError = NotFoundResponse | UnprocessableResponse


export function useExportSlaReport<TData = Awaited<ReturnType<typeof exportSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: ExportSlaReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportSlaReport>>,
          TError,
          Awaited<ReturnType<typeof exportSlaReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportSlaReport<TData = Awaited<ReturnType<typeof exportSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: ExportSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportSlaReport>>,
          TError,
          Awaited<ReturnType<typeof exportSlaReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportSlaReport<TData = Awaited<ReturnType<typeof exportSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: ExportSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The monthly SLA report as a CSV or PDF download
 */

export function useExportSlaReport<TData = Awaited<ReturnType<typeof exportSlaReport>>, TError = NotFoundResponse | UnprocessableResponse>(
 params: ExportSlaReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportSlaReport>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportSlaReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
